import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { connectSocket } from "../socket";
import SideBar from "./SideBar";
import InteractiveAvatar from "./HeyGen/InteractiveAvatar";
import Speedometer from "./Speedometer";
import { FileText, Zap, Plus, X, Paperclip, Image as ImageIcon } from "lucide-react";
import { ACCEPT_IMAGES, ACCEPT_DOCUMENTS, MAX_ATTACHMENTS, SUPPORTED_FORMATS_LABEL, IMAGE_FORMATS_LABEL, DOCUMENT_STABLE_LABEL, DOCUMENT_BETA_LABEL, DOCUMENT_FORMATS_LABEL, triageFiles, formatBytes } from "./dashboard5/utils/attachments";
import ImagePreviewModal from "./ImagePreviewModal";
import UserProfileMenu from "./UserProfileMenu";
import TSEDashboard2 from "./TSEDashboard2";
import TSEAgentRag from "./TSEAgentRag";
import TseDashboard5 from "./dashboard5/TseDashboard5";
import { ENT_BASE_PATH, entSessionPath } from "./dashboard5/sessionRoutes";
import "./dashboard5/styles/dashboard5.css";

const isImagePreviewLink = (label = "", url = "") => {
  const normalizedLabel = label.trim().toLowerCase();
  const normalizedUrl = url.trim().toLowerCase();

  return (
    normalizedLabel.includes("image") ||
    normalizedLabel.includes("screenshot") ||
    /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i.test(normalizedUrl) ||
    normalizedUrl.includes("drive.google.com/file/d/")
  );
};

// Add this mapping near the top of the file (after imports or at the start of the component):
const categoryDisplayNames = {
  'defects': 'Defects',
  'Product docs': 'Product Docs',
  'case notes': 'Case Notes'
};

// Add category color mapping
const getCategoryColor = (category) => {
  const categoryLower = category.toLowerCase();
  
  if (categoryLower === 'defects') {    return {
      bg: 'bg-blue-100',
      text: 'text-blue-800',
      border: 'border-blue-200',
      hover: 'hover:bg-blue-200',
      iconColor: 'text-blue-600'
    };
  } else if (categoryLower === 'case notes') {    return {
      bg: 'bg-purple-100',
      text: 'text-purple-800',
      border: 'border-purple-200',
      hover: 'hover:bg-purple-200',
      iconColor: 'text-purple-600'
    };
  } else if (categoryLower === 'product docs') {    return {
      bg: 'bg-yellow-100',
      text: 'text-yellow-800',
      border: 'border-yellow-200',
      hover: 'hover:bg-yellow-200',
      iconColor: 'text-yellow-600'
    };
  }
  
  // Default fallback
  return {
    bg: 'bg-gray-100',
    text: 'text-gray-800',
    border: 'border-gray-200',
    hover: 'hover:bg-gray-200',
    iconColor: 'text-gray-600'
  };
};

// Function to extract category from document name
const getCategoryFromDocumentName = (documentName) => {
  if (!documentName) return "Product Documents";
  
  const nameLower = documentName.toLowerCase();
  
  // Check for defect-related keywords FIRST (more specific)
  if (nameLower.includes('defect') || nameLower.includes('csc') || nameLower.includes('bug')) {
    return 'defects';
  }
  
  // Check for case-related keywords (but not if it contains csc)
  if ((nameLower.includes('case') || nameLower.includes('cs')) && !nameLower.includes('csc')) {
    return 'case notes';
  }
  
  // Check for product docs keywords
  if (nameLower.includes('product') || nameLower.includes('doc') || nameLower.includes('guide') || nameLower.includes('manual')) {
    return 'product docs';
  }
  
  // Default to product docs
  return 'product docs';
};

// Cache-hit badge. Blue and solid-filled so it reads as a bolt rather than as
// one more muted glyph — kept in sync with .d5-cache-indicator in dashboard5.css.
const CacheIndicator = () => (
  <span className="cache-indicator" title="Response served from cache">
    <Zap size={11} strokeWidth={2} />
  </span>
);

const ChatWindow = ({ username, onLogout, variant = "default" }) => {
  // Enterprise User View (/ai-agent): simplified layout — toolbar icons live
  // inside the input row, no bottom mode/utility bar, single user avatar (no
  // participant name chips). All chat/socket functionality is unchanged.
  const isEnterprise = variant === "enterprise";
  const navigate = useNavigate();

  // ── The enterprise chat's address ──────────────────────────────────────
  //
  // /ent-user is the greeting screen; /ent-user/<id> is a saved chat reopened
  // from the rail's Recents (App.js: the route's optional :sessionId). The
  // copilot solves the same problem with /TseDashboard5/<id>, but that route is
  // gated on AGENT_ROLES, so an enterprise user clicking a row there is bounced
  // to "/" — hence a base of its own rather than a shared one.
  const { sessionId: routeSessionId } = useParams();
  // The rail is 72px collapsed and 240px expanded (SideBar reports which), and
  // the page title is positioned absolutely next to it — so it has to move with
  // it or the two overlap the moment history is opened.
  const [railExpanded, setRailExpanded] = useState(false);

  // Enterprise split view: on submit, the centered greeting/input transitions
  // into a side-by-side view — left = TSEDashboard2 (AI Mode), right =
  // TSEAgentRag (Search Mode) — both fed the same query in parallel.
  // Seeded true when the address already names a session: reopening a chat must
  // paint the dashboard on arrival, not wait for something to be typed.
  const [splitView, setSplitView] = useState(() => isEnterprise && !!routeSessionId);
  // Last thing typed on the greeting screen. Kept only to seed the fact-aware
  // research action below; it is NOT how the message reaches the dashboard.
  const [splitQuery, setSplitQuery] = useState("");
  // The dashboard underneath writes its own send function here at mount. This
  // is the whole ghost mechanism: one call, the same one its composer makes.
  const entSendRef = useRef(null);
  const [leftPaneOpen, setLeftPaneOpen] = useState(true);
  const [rightPaneOpen, setRightPaneOpen] = useState(true);
  // Diagnostic context reported up from the AI Mode (left) pane, used by the
  // "research with more facts" action in the Search Mode (right) pane header.
  const [leftPaneContext, setLeftPaneContext] = useState("");
  const [researchRequest, setResearchRequest] = useState(null);

  // Ghost greeting attachments — handed to TseDashboard5 with the first message
  const [attachments, setAttachments] = useState([]);
  const [attachMenuOpen, setAttachMenuOpen] = useState(false);
  const [attachError, setAttachError] = useState("");
  const attachMenuWrapRef = useRef(null);
  const imageInputRef = useRef(null);
  const documentInputRef = useRef(null);

  // Close attach menu on click-outside
  useEffect(() => {
    if (!attachMenuOpen) return undefined;
    const handler = (e) => {
      if (attachMenuWrapRef.current && !attachMenuWrapRef.current.contains(e.target)) {
        setAttachMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [attachMenuOpen]);

  // Handle file selection
  const handleFilesSelected = async (files, kind) => {
    setAttachError("");
    const { accepted, rejected } = await triageFiles(files, kind, MAX_ATTACHMENTS - attachments.length);
    if (rejected.length) {
      setAttachError(rejected.map((r) => `${r.name}: ${r.reason}`).join("; "));
    }
    if (accepted.length) {
      setAttachments((prev) => [...prev, ...accepted].slice(0, MAX_ATTACHMENTS));
    }
  };

  const removeAttachment = (idx) => {
    setAttachments((prev) => {
      const removed = prev[idx];
      if (removed?.preview) URL.revokeObjectURL(removed.preview);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const runFactAwareResearch = () => {
    const facts = (leftPaneContext || "").trim();
    const base = (splitQuery || "").trim();
    if (!base && !facts) return;
    const augmented = facts
      ? `${base}\n\nUse the following diagnostic findings from the AI assistant as additional context:\n${facts}`
      : base;
    setResearchRequest((prev) => ({
      text: augmented,
      displayText: "Search with new facts",
      nonce: (prev?.nonce || 0) + 1,
    }));
  };

  // Submit from the ghost. The dashboard is already mounted and already has a
  // session, so this is a plain send into a live chat — no mount, no
  // navigation, no prop for an effect to re-fire on later.
  const sendEnterpriseQuery = () => {
    const q = message.trim();
    if (!q && !attachments.length) return;
    const send = entSendRef.current;
    if (!send) return;   // dashboard not mounted; nothing to hand the text to
    setSplitQuery(q);
    setLeftPaneOpen(true);
    setRightPaneOpen(true);
    setSplitView(true);
    setMessage("");
    // Hand both text and attachments to the dashboard
    send(q, attachments);
    // Clear attachments and revoke object URLs
    attachments.forEach((a) => a.preview && URL.revokeObjectURL(a.preview));
    setAttachments([]);
    setAttachError("");
  };

  // Return to the single centered input once both panes are closed.
  useEffect(() => {
    if (splitView && !leftPaneOpen && !rightPaneOpen) {
      setSplitView(false);
    }
  }, [splitView, leftPaneOpen, rightPaneOpen]);

  // ── The address drives the view ────────────────────────────────────────
  //
  // An id in the path means a chat was opened from the rail: show the
  // dashboard. No id means the rail's "New chat": go back to the greeting and
  // let the next message start a session, which is the pre-existing behaviour.
  //
  // Keyed on `routeSessionId` alone on purpose. A chat started by TYPING has no
  // id in the path, so this effect does not re-run when sendEnterpriseQuery
  // arms the split view — if it depended on `splitView` it would immediately
  // undo it.
  useEffect(() => {
    if (!isEnterprise) return;
    setSplitView(!!routeSessionId);
    if (routeSessionId) {
      setLeftPaneOpen(true);
      setRightPaneOpen(true);
    } else {
      setSplitQuery("");
    }
  }, [isEnterprise, routeSessionId]);

  // ── Which dashboard instance is mounted ────────────────────────────────
  //
  // Same problem App.js solves for the copilot (TseDashboard5Route): the
  // dashboard seeds its session id into a ref ONCE at mount, so switching
  // sessions has to remount it or the ref keeps pointing at the old chat. And
  // the same trap: the dashboard puts its OWN id into the address a moment
  // after mounting (onSessionIdAssigned below), and remounting on that would
  // throw away the transcript of the turn that is streaming right now.
  //
  // So the key is a mount token, not the path, and `adoptedIdRef` is how this
  // component tells "the id I was just handed" apart from "a different chat
  // someone clicked".
  const entAdoptedIdRef = useRef(null);
  const entShownIdRef = useRef(routeSessionId || null);
  const entMountKeyRef = useRef(routeSessionId ? `ent:${routeSessionId}` : "ent:new");
  // Whether the MOUNTED instance was opened onto an existing chat (rail click,
  // or a reload of /ent-user/<id>) rather than started by typing. A property of
  // the mount, not of the address: the address gains an id a tick after a typed
  // chat starts, so reading the two as the same thing tells the dashboard to
  // replay a transcript for a chat that has none. Feeds `isNewSession` only.
  const entResumedMountRef = useRef(!!routeSessionId);
  if (isEnterprise) {
    if (routeSessionId) {
      if (entAdoptedIdRef.current === routeSessionId) {
        // Our own chat, just named in the address bar by the effect below.
        // Same instance, same key — and still the typed chat it started as.
        entShownIdRef.current = routeSessionId;
      } else if (entShownIdRef.current !== routeSessionId) {
        entMountKeyRef.current = `ent:${routeSessionId}`;
        entShownIdRef.current = routeSessionId;
        entAdoptedIdRef.current = null;
        entResumedMountRef.current = true;
      }
    } else if (entShownIdRef.current !== null) {
      // Back to the greeting screen ("New chat"): the next mount must be a
      // fresh session, never the one that was just left.
      entMountKeyRef.current = `ent:new:${Date.now()}`;
      entShownIdRef.current = null;
      entAdoptedIdRef.current = null;
      entResumedMountRef.current = false;
    }
  }

  // The dashboard names an embedded session at mount (sessionStorage, or the id
  // passed down here), so this is the moment the address can catch up with it.
  // Recorded in adoptedIdRef FIRST so the block above reads the navigation as
  // this instance's own and leaves it mounted.
  const handleEntSessionIdAssigned = (id) => {
    if (!isEnterprise || !id) return;
    entAdoptedIdRef.current = id;
    if (routeSessionId === id) return;
    navigate(entSessionPath(id), { replace: true });
  };
  const [socket, setSocket] = useState(null);
  const [message, setMessage] = useState("");
  const [mode, setMode] = useState("diagnostic");
  const [searchSubMode, setSearchSubMode] = useState("perplexity");
  const [diagnosticMessages, setDiagnosticMessages] = useState([]);
  const [searchMessages, setSearchMessages] = useState([]);
  const [searchLoadingStatus, setSearchLoadingStatus] = useState(null);
  const [copilotMessages, setCopilotMessages] = useState([]); // Add copilot messages state
  const [documentSummaries, setDocumentSummaries] = useState({}); // Store AI summaries by document path
  // Computed messages after mode is initialized
  const messages = mode === "diagnostic" ? diagnosticMessages : mode === "search" ? searchMessages : copilotMessages;
  const setMessages = mode === "diagnostic" ? setDiagnosticMessages : mode === "search" ? setSearchMessages : setCopilotMessages;
  
  // Update modeRef when mode changes and add debug logging
  useEffect(() => {
    modeRef.current = mode; // Keep ref in sync
    console.log("🔄 Mode changed to:", mode);
    console.log("🔄 Messages count:", messages.length);
    console.log("🔄 Diagnostic:", diagnosticMessages.length, "Search:", searchMessages.length, "Copilot:", copilotMessages.length);
  }, [mode, messages.length, diagnosticMessages.length, searchMessages.length, copilotMessages.length]);

  const [isWaitingForCommand, setIsWaitingForCommand] = useState(false);
  const [isWaitingForDecision, setIsWaitingForDecision] = useState(false);
  const [inRecommendation, setInRecommendation] = useState(false);
  const [recommendationStartIdx, setRecommendationStartIdx] = useState(null);
  const chatBodyRef = useRef(null);
  const modeRef = useRef(mode); // Track current mode for socket handlers

  const [currentSearchId, setCurrentSearchId] = useState(null); // Tracks current search
  const [searchSourcesMap, setSearchSourcesMap] = useState({}); // Stores sources per search
  const [generatedOverviews, setGeneratedOverviews] = useState(new Set()); // Track which documents have generated overviews
  const [queryOverviews, setQueryOverviews] = useState({}); // Store overviews per query

  // Simple typing indicator for diagnostic mode
  const [showTypingIndicator, setShowTypingIndicator] = useState(false);
  // Add escalation state
  const [escalationActive, setEscalationActive] = useState(false);

  // Function to get current agent name
  const getCurrentAgentName = () => {
    const bot = participants.find(p => p.role === "bot");
    if (bot && bot.name) {
      return bot.name;
    }
    // Fallback names based on session type or default
    return "AI Agent";
  };

  // Image Component
  const [zoomImageUrl, setZoomImageUrl] = useState(null);
  const handleImageClick = (url) => setZoomImageUrl(url);
  const closeZoom = () => setZoomImageUrl(null);

  // Video Chat State
  const [videoOpen, setVideoOpen] = useState(false);
  const videoRef = useRef(null);

  // HeyGen Avatar State
  const [avatarActive, setAvatarActive] = useState(false); // Avatar session active
  const [avatarPanelOpen, setAvatarPanelOpen] = useState(false); // Panel visibility
  const [avatarStatus, setAvatarStatus] = useState("idle");
  const avatarRef = useRef(null);

  // Debug avatarActive changes
  useEffect(() => {
    console.log("🎭 avatarActive changed to:", avatarActive);
    console.trace("Stack trace for avatarActive change:");
  }, [avatarActive]);

  // Debug avatarPanelOpen changes
  useEffect(() => {
    console.log("🎭 avatarPanelOpen changed to:", avatarPanelOpen);
  }, [avatarPanelOpen]);

  // Dynamic participants for avatars
  const [participants, setParticipants] = useState([]);
  const [botAvatarId, setBotAvatarId] = useState(null); // Track bot's avatar ID
  const [aiAgentMenuOpen, setAiAgentMenuOpen] = useState(false);
  const aiAgentMenuRef = useRef(null);
  const [tseAgentName, setTseAgentName] = useState(localStorage.getItem("TseAgentName") || "");

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (aiAgentMenuRef.current && !aiAgentMenuRef.current.contains(e.target)) {
        setAiAgentMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  //const [escalationActive, setEscalationActive] = useState(false);
  const [agentNotification, setAgentNotification] = useState("");

  const [sentiment, setSentiment] = useState(10);
  const [prevSentiment, setPrevSentiment] = useState(10);
  const [sessionStatus, setSessionStatus] = useState({ hasActiveSession: false });
  const [lastDiagnosticState, setLastDiagnosticState] = useState(null);

  // const [sources, setSources] = useState([
  //   {
  //     title: "A Guide to Network Troubleshooting",
  //     url: "https://www.comptia.org/content/guides/a-guide-to-network-troubleshooting",
  //     favicon: "https://www.comptia.org/favicon.ico"
  //   },
  //   {
  //     title: "Network Troubleshooting - Methodology, Steps &...",
  //     url: "https://www.softwaretestinghelp.com/network-troubleshooting/",
  //     favicon: "https://www.softwaretestinghelp.com/favicon.ico"
  //   },
  //   {
  //     title: "Network Troubleshooting: Documentation - Dummies",
  //     url: "https://www.dummies.com/article/technology/information-technology/networking/network-troubleshooting-documentation-207865/",
  //     favicon: "https://www.dummies.com/favicon.ico"
  //   }
  // ]);

  const [diagnosticSources, setDiagnosticSources] = useState([]);
  const [ragSources, setRagSources] = useState([]);

  // Sources panel state
  const [sourcesPanelOpen, setSourcesPanelOpen] = useState(false);
  const [userHasSentMessage, setUserHasSentMessage] = useState(false);
  const [sourcesVisible, setSourcesVisible] = useState(false);
  const [visibleSourceCount, setVisibleSourceCount] = useState(0);

  // Add new state for sidebar mode
  const [sidebarMode, setSidebarMode] = useState("sources"); // "sources" or "search"
  const [searchQuery, setSearchQuery] = useState("");

  // Add support type state with a welcome message
  const [supportType, setSupportType] = useState("WELCOME TO ARTA AI SUPPORT");
  const [displayTitle, setDisplayTitle] = useState("WELCOME TO ARTA AI SUPPORT");
  const [isTyping, setIsTyping] = useState(false);
  const [isTitleSet, setIsTitleSet] = useState(false);

  // Typewriter effect hook
  useEffect(() => {
    if (supportType === displayTitle) return;
    
    setIsTyping(true);
    let currentIndex = 0;
    const targetText = supportType;
    
    const typingInterval = setInterval(() => {
      if (currentIndex <= targetText.length) {
        setDisplayTitle(targetText.substring(0, currentIndex));
        currentIndex++;
      } else {
        clearInterval(typingInterval);
        setIsTyping(false);
      }
    }, 50); // Adjust speed here (lower = faster)
    
    return () => clearInterval(typingInterval);
  }, [supportType]);

  // Add function to get title from message
  const getTitleFromMessage = (message) => {
    const msg = message.toLowerCase();
    if (msg.includes("network")) return "NETWORK ISSUE";
    if (msg.includes("server")) return "SERVER ISSUE";
    if (msg.includes("graphiant")) return "GRAPHIANT ISSUE";
    return "WELCOME TO ARTA AI SUPPORT"; // Default title when no keywords found
  };

  // Auto-open sources panel when sources are available
  useEffect(() => {
    if (diagnosticSources.length > 0) {
      setSidebarMode("sources"); // Set to sources mode (not search mode)
      setSourcesPanelOpen(true);
      setSourcesVisible(true);
      setVisibleSourceCount(0);
    }
  }, [diagnosticSources.length]);

  // Keep the animation effect for sources when they become visible
  useEffect(() => {
    if (sourcesVisible && visibleSourceCount < diagnosticSources.length) {
      const timeout = setTimeout(() => {
        setVisibleSourceCount((count) => count + 1);
      }, 250);
      return () => clearTimeout(timeout);
    }
  }, [sourcesVisible, visibleSourceCount, diagnosticSources.length]);

  useEffect(() => {
    const s = connectSocket();
    setSocket(s);

    s.on("connect", () => {
      console.log("✅ Socket connected:", s.id);
      // Join a room with session ID and role
      const sessionId = s.id;
      const effectiveUsername = username || localStorage.getItem("username") || "User";
      console.log("calling join room from connect")
      s.emit("joinRoom", { sessionId, role: "user", name: effectiveUsername });
      
      // Join copilot room as user
      console.log("🔌 Joining copilot room as user...");
      s.emit("joinCopilotRoom", {
        room_id: "copilot_room",
        role: "user",
        name: effectiveUsername
      });
      
      // Request session status on connect
      setTimeout(() => {
        s.emit("getSessionStatus");
      }, 500); // Small delay to ensure session is set up
    });

    s.on("agentJoined", () => {
      console.log("Agent Joined Escalation - A Live agent has joined the chat");
      setEscalationActive(true); // Block typing indicator after escalation
      setShowTypingIndicator(false); // Immediately hide typing indicator
      setAgentNotification("A live agent has joined the chat.");
      setTimeout(() => setAgentNotification(""), 5000);
    });

    s.on("escalationAlert", () => {
      setEscalationActive(true);
      setShowTypingIndicator(false);
    });

    s.on("chatMessage", (msg) => {
      const { text, response, done, sender, queryType } = msg;
      // Update support type if queryType is received and title hasn't been set yet
      if (queryType && !isTitleSet && queryType !== "network") {
        setSupportType(queryType);
        console.log("📋 Support type updated to:", queryType);
      }
      
      const displayText = text || response || "🤖 AI Agent: [No response]";
      console.log("📨 Received chatMessage:", { text, response, sender, timestamp: msg.timestamp, queryType });

      // The opening line REPLACES the previous one instead of stacking under
      // it. The server sends a fresh greeting — new random agent and all — on
      // every joinRoom and every reset, and this component outlives all of
      // them (a "New chat" remounts only the dashboard), so appending left a
      // column of eight introductions from five different people above one
      // empty composer. Marked by the server (`greeting: true`) rather than
      // matched on text, which is randomised.
      const isGreeting = !!msg.greeting;
      const entry = {
        text: displayText,
        sender: sender || "bot",
        timestamp: msg.timestamp,
        cache_hit: msg.cache_hit || false,
        ...(isGreeting ? { isGreeting: true } : {}),
      };

      if (mode === "diagnostic" && (sender === "bot" || !sender)) {
        setShowTypingIndicator(false);
      }
      setDiagnosticMessages((prev) => (
        isGreeting
          ? [...prev.filter((m) => !m.isGreeting), entry]
          : [...prev, entry]
      ));

      // SIMPLE APPROACH: If there's an avatar ref and this is a bot message, send it directly
      if (avatarRef.current && (sender === "bot" || !sender) &&
      !displayText.includes("Troubleshooting Image")
      ) {
        console.log("🎤 Direct sending to avatar:", displayText);
        
        avatarRef.current.sendMessage(displayText);
      }
      
      if (
        displayText.includes("Please execute the following command") ||
        displayText.includes("Using the below command's output")
      )
        setIsWaitingForCommand(true);

      if (
        displayText.includes("Would you like to proceed") ||
        displayText.includes("Shall I close this ticket")
      )
        setIsWaitingForDecision(true);

      if (done) {
        setIsWaitingForCommand(false);
        setIsWaitingForDecision(false);
      }
    });

    // Handle full conversation history (for agent join)
    s.on("conversationHistory", (historyArr) => {
      // historyArr: [{text, sender}, ...]
      setMessages(historyArr);
    });

    // ===== COPILOT (OLD) - Listen for copilot messages =====
    // s.on("copilotMessage", (data) => {
    //   if (modeRef.current === "copilot") {
    //     const messageData = {
    //       text: data.text,
    //       sender: data.sender || "copilot",
    //       timestamp: data.timestamp || Date.now()
    //     };
    //     setCopilotMessages((prev) => [...prev, messageData]);
    //   }
    // });
    
    // ===== TESTPILOT (NEW) - Listen for testpilot messages =====
    s.on("testpilotMessage", (data) => {
      console.log("🤖 Received testpilot message:", data);
      
      // Check if this is a /clear command response - keep chat visible, only reset interaction state
      if (data.session_cleared || data.clear_all_state) {
        console.log("🔄 Backend Conversation cleared! — keeping chat history visible");
        
        // Reset interaction-state flags (NOT the messages array)
        setIsWaitingForCommand(false);
        setIsWaitingForDecision(false);
        setInRecommendation(false);
        setRecommendationStartIdx(null);
        setShowTypingIndicator(false);
        setIsTyping(false);
        
        // Clear sources if any
        setRagSources([]);
        setSourcesVisible(false);
        setVisibleSourceCount(0);
        
        // Reset search-related state if in search mode
        setSearchQuery("");
        setCurrentSearchId(null);
        setSearchSourcesMap({});
        setGeneratedOverviews(new Set());
        setQueryOverviews({});
        
        console.log("✅ Backend state cleared — chat history preserved on screen");
        // Don't return — let the confirmation message (if any) get appended below
        if (data.silent) return;
      }
      
      // Only add message if it has text and is not a silent clear
      if (modeRef.current === "copilot" && data.text && !data.silent) {
        const messageData = {
          text: data.text,
          sender: data.sender || "testpilot",
          timestamp: data.timestamp || Date.now(),
          cache_hit: data.cache_hit || false,
        };
        setCopilotMessages((prev) => [...prev, messageData]);
      }
    });

    // ===== TESTPILOT - Conversation cleared! confirmation =====
    s.on("testpilotCleared", (data) => {
      console.log("🧹 TestPilot Conversation cleared!:", data);
      if (data.success) {
        setCopilotMessages([{
          text: "🧹 Context cleared. Starting a fresh conversation!",
          sender: "system",
          timestamp: Date.now()
        }]);
      }
    });
    
    // Handle liveAgentMessage (TSE agent messages)
    s.on("liveAgentMessage", (msg) => {
      // msg: {text, sender, agentName, timestamp}
      console.log("👨‍💼 Received liveAgentMessage:", msg);
      console.log("👨‍💼 Current mode (ref):", modeRef.current);

      const messageData = {
        text: msg.text,
        sender: msg.sender || "agent",
        agentName: msg.agentName,
        timestamp: msg.timestamp || Date.now(),
      };

      if (modeRef.current === "copilot") {
        console.log("👨‍💼 Adding to copilot messages");
        setCopilotMessages((prev) => [...prev, messageData]);
      } else {
        console.log("👨‍💼 Adding to diagnostic messages");
        setDiagnosticMessages((prev) => [...prev, {
          text: msg.text,
          sender: msg.sender || "agent",
          agentName: msg.agentName || "Agent",
          timestamp: msg.timestamp || Date.now(),
          type: "text"
        }]);
      }
    });

    // Handle TSE agent joining notification
    s.on("tseAgentJoined", (data) => {
      console.log("👨‍💻 Agent Joined event received:", data);
      let tseAgentPresent = "true";
      localStorage.setItem('IsTseAgentPresent', tseAgentPresent);
      if (data && data.agentName) {
        localStorage.setItem('TseAgentName', data.agentName);
        setTseAgentName(data.agentName);
      }
      if (modeRef.current === "copilot") {
        // Add system message that TSE agent has joined
        const systemMessage = {
          // Remove hardcoded Agent Name Chris
          // "TSE" is our internal name for the role, and this line is read by
          // the customer. When the agent's real name is missing the fallback
          // has to be something they would recognise, not an acronym.
          text: `🎉 `+ capitalize((data && data.agentName) || "A support agent") + ` has joined your session and is ready to assist you!`,
          sender: "system",
          timestamp: Date.now(),
        };
        setCopilotMessages((prev) => [...prev, systemMessage]);
      }
    });

    // Listen for dynamic room participants
    s.on("roomParticipants", (list) => {
      setParticipants(list);
      // Extract bot's avatar_id if available
      const bot = list.find(p => p.role === "bot");
      if (bot && bot.avatar_id) {
        setBotAvatarId(bot.avatar_id);
        console.log("🎭 Bot avatar ID set to:", bot.avatar_id);
      }
      if (bot?.name) {
        localStorage.setItem("ArtaAiAgentName", bot.name);
      }
    });

    s.on("sentimentUpdate", (data) => {
      console.log("[Frontend] Received sentimentUpdate:", data);
      if (data && typeof data.sentiment === "number") {
        setSentiment(current => {
          setPrevSentiment(current);
          return data.sentiment;
        });
      }
    });

    // Listen for search responses (RAG system responses)
    // s.on("searchResponse", (msg) => {
    //   const { text, sender, timestamp, query, rag_sources } = msg;
    //   console.log("📨 Received searchResponse:", { text, sender, timestamp, query });
      
    //   const displayText = text || "🤖 Search: [No response]";
      


    //   // Format sources (if any)
    //   const formattedSources = (rag_sources || []).map((item) => {
    //     const relativePath = typeof item === "string" ? item : item.link;
    //     const fullUrl = `https://api.artasupport.com/static/images/${relativePath}`;
    //     return {
    //       link: fullUrl || "No URL found.",
    //       name: item.name || "No File name found.",
    //       summary: item.summary || "No Summary available.",
    //       icon: "pdf",
    //     };
    //   });
    //   // Only update search messages for searchResponse events
    //   setSearchMessages((prev) => [...prev, { 
    //     text: displayText, 
    //     sender: sender || "bot", 
    //     timestamp: timestamp,
    //     isSearchResponse: true,
    //     sources: formattedSources || [] // Add sources field here
 
    //   }]);

    // });
    s.on("searchStatus", (msg) => {
      setSearchLoadingStatus(msg.message);
    });

    s.on("searchResponse", (msg) => {
      setSearchLoadingStatus(null);
      const { text, sender, timestamp, query, rag_sources, complete } = msg;

      setSearchMessages((prev) => {
        const updated = [...prev];
        const lastIdx = updated.length - 1;

        // Case 1: Update existing streaming message
        if (lastIdx >= 0 && updated[lastIdx].isStreaming) {
          const lastMsg = updated[lastIdx];

          const newText = complete ? (text || lastMsg.text) : lastMsg.text + text;

          updated[lastIdx] = {
            ...lastMsg,
            text: newText,
            isStreaming: !complete,
            timestamp: complete ? timestamp : lastMsg.timestamp,
            sources: complete
              ? (rag_sources || []).map((item) => {
                  const rawLink = typeof item === "string" ? item : item.link;
                  const isFullUrl = typeof rawLink === "string" && (rawLink.startsWith("http://") || rawLink.startsWith("https://"));
                  const link = isFullUrl ? rawLink : `https://staging-api.artasupport.com/static/images/${rawLink}`;
                  return {
                    link,
                    name: item.name || "No File name found.",
                    summary: item.summary || "No Summary available.",
                    category: item.category || "Product Documents",
                    icon: item.icon || "pdf",
                    causal: Boolean(typeof item === "object" && item?.causal),
                  };
                })
              : lastMsg.sources,
          };

          return updated;
        }

        // Case 2: No existing streaming message, so add new one
        return [
          ...prev,
          {
            text,
            sender,
            timestamp,
            isSearchResponse: true,
            isStreaming: !complete,
            sources: (rag_sources || []).map((item) => {
              const rawLink = typeof item === "string" ? item : item.link;
              const isFullUrl = typeof rawLink === "string" && (rawLink.startsWith("http://") || rawLink.startsWith("https://"));
              const link = isFullUrl ? rawLink : `https://staging-api.artasupport.com/static/images/${rawLink}`;
              return {
                link,
                name: item.name || "No File name found.",
                summary: item.summary || "No Summary available.",
                category: item.category || "Product Documents",
                icon: item.icon || "pdf",
                causal: Boolean(typeof item === "object" && item?.causal),
              };
            }),
          },
        ];
      });
    });

    // Listen for diagnostic sources

    s.on("diagnosticSources", (msg) => {
    const { sources = [], timestamp, sender, error } = msg;

    console.log("📨 Received diagnosticSources:", sources);

    if (error) {
      console.error("❌ Error in diagnosticSources:", error);
      return;
    }

    setDiagnosticSources((prev) => {
      const newSources = [...prev];

      sources.forEach((url) => {
        if (!newSources.some(src => src.url === url)) {
          const fileName = url.split("/").pop();
          newSources.push({
            title: fileName || "View Document",
            url: url,
            summary: "",
            icon: "pdf"  // or "file" if you want generic
          });
        }
      });

      return newSources;
    });


  });


    // Listen for session status updates
    s.on("sessionStatus", (status) => {
      console.log("📊 Received sessionStatus:", status);
      setSessionStatus(status);
      
      // Clear last diagnostic state if session is no longer active  
      if (!status.hasActiveSession) {
        setLastDiagnosticState(null);
      }
    });

    // Listen for document summaries
    s.on("documentSummary", (data) => {
      const { documentPath, summary, error } = data;
      console.log("📄 Received documentSummary:", { documentPath, summary: summary?.slice(0, 100) + "...", error });
      
      if (error) {
        console.error("❌ Error generating summary:", error);
        setDocumentSummaries(prev => {
          console.log("📄 Setting error summary for:", documentPath);
          return {
            ...prev,
            [documentPath]: "Unable to generate summary for this document."
          };
        });
      } else {
        setDocumentSummaries(prev => {
          console.log("📄 Setting success summary for:", documentPath);
          return {
            ...prev,
            [documentPath]: summary
          };
        });
      }
    });
    console.log("About to Disconnect SearchResponse")
    return () => s.disconnect();
  }, [username]); // Removed mode - using useRef instead to avoid socket recreation

  // Avatar event handlers
  const handleAvatarStatusChange = (status) => {
    console.log("Avatar status:", status);
    setAvatarStatus(status);
    
    // Update avatarActive based on actual session state
    if (status === "connected") {
      setAvatarActive(true);
      console.log("🎭 Avatar connected, setting avatarActive to true");
    } else if (status === "disconnected") {
      setAvatarActive(false);
      console.log("🎭 Avatar disconnected, setting avatarActive to false");
    }
  };

  // Function to send message to avatar
  const sendMessageToAvatar = (message) => {
    console.log("🎭 sendMessageToAvatar called:", { message, avatarRefExists: !!avatarRef.current, avatarActive });
    if (avatarRef.current && avatarActive) {
      console.log("✅ Calling avatar.sendMessage");
      avatarRef.current.sendMessage(message);
    } else {
      console.log("❌ Avatar ref or avatarActive not ready:", { avatarRefExists: !!avatarRef.current, avatarActive });
    }
  };

  useEffect(() => {
    // Handle auto-scroll based on message sender and mode
    if (messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      
      setTimeout(() => {
        if (lastMessage.sender === 'user' || lastMessage.sender === username) {
          // User message: scroll to bottom (all modes)
          chatBodyRef.current?.scrollTo({
            top: chatBodyRef.current.scrollHeight,
            behavior: "smooth",
          });
        } else if ((lastMessage.sender === 'bot' || lastMessage.sender === 'system' || lastMessage.sender === 'agent')) {
          // Bot, system, or live agent message: auto-scroll based on mode
          const chatBody = chatBodyRef.current;
          if (chatBody) {
            const currentScrollTop = chatBody.scrollTop;
            const scrollHeight = chatBody.scrollHeight;
            const clientHeight = chatBody.clientHeight;
            
            if (mode === "diagnostic") {
              // Diagnostic mode: show about 50% of the new message
              const targetScrollTop = Math.min(
                currentScrollTop + (clientHeight * 0.5),
                scrollHeight - clientHeight // Don't go past the bottom
              );
              
              chatBody.scrollTo({
                top: targetScrollTop,
                behavior: "smooth",
              });
            } else if (mode === "copilot") {
              // Copilot mode: always scroll to bottom for new messages
              chatBody.scrollTo({
                top: scrollHeight - clientHeight,
                behavior: "smooth",
              });
            } else {
              // Search mode: don't auto-scroll for bot responses
            }
          }
        }
      }, 100); // Small delay to ensure DOM is updated
    }
  }, [messages, username, mode]);



  // Video chat handlers
  const handleStartVideo = async () => {
    setVideoOpen(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
    } catch (err) {
      alert("Could not access webcam: " + err.message);
      setVideoOpen(false);
    }
  };

  const handleStopVideo = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const tracks = videoRef.current.srcObject.getTracks();
      tracks.forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }
    setVideoOpen(false);
  };

  // Auto-resize textarea function
  const autoResizeTextarea = (textarea) => {
    if (textarea) {
      // Reset height to calculate scroll height properly
      textarea.style.height = '28px';
      
      // Calculate new height based on content
      const scrollHeight = textarea.scrollHeight;
      const newHeight = Math.min(Math.max(scrollHeight, 28), 200); // Min 28px, max 200px
      
      // Apply the new height
      textarea.style.height = newHeight + 'px';
      
      // Keep textarea scroll position at top when it expands
      if (newHeight > 28) {
        setTimeout(() => {
          textarea.scrollTop = 0;
        }, 10);
      }
    }
  };

  // Helper function to parse timestamps specifically for Search mode
  const parseSearchTimestamp = (timestamp) => {
    if (!timestamp) return null;
    if (typeof timestamp === "number") return new Date(timestamp);
    if (typeof timestamp === "string") {
      // Handle the problematic format: "2025-07-12T20:44:28.027644"
      // This format has microseconds (6 digits) and no timezone indicator
      const microsecondsMatch = timestamp.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})\.(\d{3})\d{3}$/);
      if (microsecondsMatch) {
        // Convert microseconds to milliseconds and add UTC indicator
        return new Date(`${microsecondsMatch[1]}.${microsecondsMatch[2]}Z`);
      }
      
      // If already ISO with Z or timezone, let Date handle it
      if (timestamp.endsWith("Z") || timestamp.includes("+") || timestamp.includes("-")) {
        return new Date(timestamp);
      }
      
      // If no timezone indicator, treat as UTC
      if (timestamp.includes("T")) {
        return new Date(timestamp + "Z");
      }
      
      // Fallback
      return new Date(timestamp);
    }
    return null;
  };

  // Format timestamp function
  const formatTimestamp = (timestamp, isSearchMode = false) => {
    if (!timestamp) return '';
    
    let date;
    if (isSearchMode) {
      // Use special parsing for search mode timestamps
      date = parseSearchTimestamp(timestamp);
    } else {
      // Use standard parsing for diagnostic mode (keep existing behavior)
      date = new Date(timestamp);
    }
    
    if (!date || isNaN(date.getTime())) {
      console.warn("🕒 Invalid timestamp:", timestamp);
      return '';
    }
    
    const formatted = date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
    return formatted;
  };

  // Request AI summary for a document
  const requestDocumentSummary = (documentPath, queryId = null) => {
    console.log("📄 requestDocumentSummary called with:", documentPath, "queryId:", queryId);
    console.log("📄 Current documentSummaries:", documentSummaries);
    console.log("📄 Generated overviews:", generatedOverviews);
    console.log("📄 Socket exists:", !!socket);

    // Use a composite key for per-query independence
    const overviewKey = queryId ? `${documentPath}__${queryId}` : documentPath;

    if (!generatedOverviews.has(overviewKey) && socket) {
      console.log("📄 Emitting generateDocumentSummary for:", documentPath, "queryId:", queryId);
      socket.emit("generateDocumentSummary", { documentPath, queryId });
      setGeneratedOverviews(prev => new Set([...prev, overviewKey]));
    } else {
      console.log("📄 Skipping request - already generated overview or no socket");
    }
  };

  // Expose expandImage function globally for backend-generated HTML
  useEffect(() => {
    window.expandImage = (url) => {
      setZoomImageUrl(url);
    };
    return () => {
      delete window.expandImage;
    };
  }, []);

  // Format bot messages for better readability
  const formatBotMessage = (text, isSearchResponse = false, currentMode = null, sources = []) => {
    if (!text || typeof text !== 'string') return text;

    // ── Copilot mode: clean, plain-text formatting (no markdown artifacts) ──
    if (currentMode === "copilot") {
      let fmt = text;

      // 1. Google Drive image links → clickable "image"
      fmt = fmt.replace(
        /!\[\]\[?(https?:\/\/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)\/view[^\]]*)\]?/g,
        '<a href="javascript:void(0);" class="image-link text-blue-600 hover:text-blue-800 underline" data-image-url="https://drive.google.com/uc?export=view&id=$2" style="cursor:pointer;">image</a>'
      );
      fmt = fmt.replace(
        /\[image\]\((https?:\/\/[^)]+)\)/g,
        '<a href="javascript:void(0);" class="image-link text-blue-600 hover:text-blue-800 underline" data-image-url="$1" style="cursor:pointer;">image</a>'
      );

      // 2. Strip heading markers (###, ##, #)
      fmt = fmt.replace(/^#{1,3}\s+/gm, '');

      // 3. Convert bold markers **text** → <strong>text</strong> (safety net if LLM still uses markdown)
      fmt = fmt.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

      // 4. Strip italic markers *text* → text
      fmt = fmt.replace(/\*(.*?)\*/g, '$1');

      // 5. Strip bullet dashes / dots at start of line
      fmt = fmt.replace(/^\s*[-•]\s+/gm, '  ');

      // 6. Markdown links [text](url) → proper <a> tags
      fmt = fmt.replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g, (match, label, url) => {
        if (isImagePreviewLink(label, url)) {
          return `<a href="javascript:void(0);" class="image-link text-blue-600 hover:text-blue-800 underline" data-image-url="${url}" style="cursor:pointer;">${label}</a>`;
        }

        return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 underline">${label}</a>`;
      });

      // 7. Bare URLs → clickable (skip URLs already inside href="...")
      // Stop at &lt; / &gt; HTML entities so escaped angle brackets aren't swallowed into the URL
      fmt = fmt.replace(/(?<!")\b(https?:\/\/(?:[^\s<>)"&]|&(?!lt;|gt;))+)(?!")/gi, (match) => {
        return `<a href="${match}" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 underline">${match}</a>`;
      });

      // 8. Style known section labels (Response Type is excluded — not shown)
      fmt = fmt.replace(
        /^(Diagnostic Procedure|Current State|Current Position|Interpretation|Next Step|After This|Root Cause|Available Fixes|Workaround Options|Permanent Fix|Important|What we are checking|Steps|What to Observe|Next):/gm,
        '<strong>$1:</strong>'
      );

      // Remove any stray "Response Type: ..." lines the LLM might still emit
      fmt = fmt.replace(/^Response Type:.*$/gm, '');

      // 9. Newlines → <br>
      fmt = fmt.replace(/\n/g, '<br>');
      fmt = fmt.replace(/(<br>){3,}/g, '<br><br>');

      return fmt;
    }

    let formatted = text;

    // 1. Format markdown tables to HTML tables
    formatted = formatted.replace(/Markdown Summary Table/gi, 'Summary Table');
    formatted = formatted.replace(/\(with Line Breaks\)/gi, '');

    // Detect markdown table blocks
    formatted = formatted.replace(/```[\s\S]*?\n([\s\S]*?\|[\s\S]*?\|[\s\S]*?)```/g, (match, tableContent) => {
      // Split lines and filter out empty lines
      const lines = tableContent.trim().split('\n').filter(line => line.trim());
      if (lines.length < 2) return match; // Not a valid table
      // Find header and separator
      const header = lines[0].split('|').map(cell => cell.trim()).filter(Boolean);
      const separator = lines[1];
      const rows = lines.slice(2).map(line => line.split('|').map(cell => cell.trim()).filter(Boolean));
      // Build HTML table
      let html = '<table class="border border-gray-300 rounded-lg overflow-hidden my-2 text-sm" style="table-layout:auto;width:auto;max-width:100%;"><thead><tr>';
      header.forEach(cell => {
        html += `<th class='bg-gray-100 border-b border-gray-300 px-3 py-2 font-semibold text-left'>${cell}</th>`;
      });
      html += '</tr></thead><tbody>';
      rows.forEach(row => {
        html += '<tr>';
        row.forEach(cell => {
          // Detect markdown links [text](url) → clickable anchor
          const mdLinkMatch = cell.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
          const urlMatch = cell.match(/^https?:\/\/[\w\-._~:/?#\[\]@!$&'()*+,;=%]+$/i);
          let cellContent = cell;
          if (mdLinkMatch) {
            const [, label, url] = mdLinkMatch;
            cellContent = `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-blue-600 underline">${label}</a>`;
          } else if (urlMatch) {
            cellContent = `<a href="${cell}" target="_blank" rel="noopener noreferrer" class="text-blue-600 underline">Link</a>`;
          }
          html += `<td class='border-b border-gray-200 px-3 py-2' style='max-width:220px;white-space:normal;word-break:break-word;'>${cellContent}</td>`;
        });
        html += '</tr>';
      });
      html += '</tbody></table>';
      return html;
    });

    // 2. Markdown headings → styled HTML
    formatted = formatted.replace(/^###\s+(.+)$/gm, '<h3 style="font-size:15px;font-weight:700;margin:16px 0 8px 0;color:#1e293b;">$1</h3>');
    formatted = formatted.replace(/^##\s+(.+)$/gm, '<h2 style="font-size:16px;font-weight:700;margin:16px 0 8px 0;color:#1e293b;">$1</h2>');

    // 3. Format numbered lists - more conservative approach
    formatted = formatted.replace(/(\d+\..*?)(\n\s*\n)(\d+\.\s)/gs, '$1<br/><br/>$3');

    // 4. Bold text: **text** → <strong>text</strong>
    formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

    // 4. Color-coded severity levels with better styling
    formatted = formatted.replace(/\(Severity:\s*(Critical)\)/gi, '<span style="color: #dc2626; font-weight: 600;">(Severity: $1)</span>');
    formatted = formatted.replace(/\(Severity:\s*(High)\)/gi, '<span style="color: #ea580c; font-weight: 600;">(Severity: $1)</span>');
    formatted = formatted.replace(/\(Severity:\s*(Medium)\)/gi, '<span style="color: #d97706; font-weight: 600;">(Severity: $1)</span>');
    formatted = formatted.replace(/\(Severity:\s*(Low)\)/gi, '<span style="color: #16a34a; font-weight: 600;">(Severity: $1)</span>');

    // 5. Add line breaks before standalone questions that start with capital letters
    formatted = formatted.replace(/([.!])\s+((?:Would you like|Please select|Do you want|Shall I|Would you prefer)[^?]*\?)/gi, '$1<br/><br/>$2');

    // 6. Inline citation pills: [1], [2], etc. → Perplexity-style domain/name pill with hover popover
    formatted = formatted.replace(/\[(\d{1,2})\]/g, (match, num) => {
      const idx = parseInt(num, 10) - 1;
      const src = sources && sources[idx];
      if (!src) {
        return '';
      }
      const name = (src.name || '').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
      const summary = (src.summary || '').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
      const category = (src.category || '').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
      const link = src.link || '';
      let host = '';
      try { host = link ? new URL(link).host.replace(/^www\./, '') : ''; } catch (_) {}
      const truncHost = host.length > 35 ? host.slice(0, 35) + '...' : host;
      const pillLabel = link || name;
      return `<a href="${link || '#'}" target="_blank" rel="noopener noreferrer" class="citation-hover-wrap" style="position:relative;display:inline-flex;align-items:center;padding:1px 7px;border-radius:4px;background:#f3f4f6;color:#6b7280;font-size:11px;font-weight:500;margin:0 2px;vertical-align:middle;cursor:pointer;white-space:nowrap;text-decoration:none;overflow:hidden;text-overflow:ellipsis;max-width:100%;display:inline-block;">${pillLabel}<span class="citation-popover" style="display:none;position:absolute;bottom:calc(100% + 8px);left:0;width:280px;background:white;border:1px solid #e2e8f0;border-radius:12px;box-shadow:0 4px 16px rgba(0,0,0,0.12);padding:14px;z-index:50;text-align:left;cursor:default;white-space:normal;"><span style="display:block;font-size:11px;color:#6b7280;font-weight:600;margin-bottom:4px;">${category}</span><span style="display:block;font-size:13px;font-weight:700;color:#1e293b;margin-bottom:6px;line-height:1.3;">${name}</span><span style="display:block;font-size:12px;color:#64748b;line-height:1.4;margin-bottom:8px;">${summary.length > 120 ? summary.slice(0, 120) + '...' : summary}</span></span></a>`;
    });

    // 7. Markdown links [text](url) → clickable anchor
    formatted = formatted.replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 underline">$1</a>'
    );

    // 8. Horizontal rules --- → <hr>
    formatted = formatted.replace(/^---$/gm, '<hr style="border:none;border-top:1px solid #e5e7eb;margin:16px 0;">');

    // 9. Preserve existing line breaks and convert them to HTML
    formatted = formatted.replace(/\n/g, '<br/>');

    // 9. Clean up multiple consecutive br tags
    formatted = formatted.replace(/(<br\/>){3,}/g, '<br/><br/>');

    // Structure 'Analysis:' blocks: bold header, line break, then analysis text, and highlight code/values, but do NOT wrap in a card or add icon
    formatted = formatted.replace(/(^|<br\/>)(Analysis:)([\s\S]*?)(?=(<br\/>|$))/gi, (match, br, header, body) => {
      let highlightedBody = body.trim();
      // Only highlight in the analysis body (not in HTML tags)
      // Backticks: `code`
      highlightedBody = highlightedBody.replace(/`([^`]+)`/g, '<span style="font-weight:bold;font-style:italic;background:#f3f4f6;padding:1px 4px;border-radius:4px;">$1</span>');
      // Single quotes: 'value' (not part of a word like they're)
      highlightedBody = highlightedBody.replace(/(?<=\s|^)'([^'\s][^']*?)'(?!\w)/g, '<span style="font-weight:bold;font-style:italic;background:#f3f4f6;padding:1px 4px;border-radius:4px;">$1</span>');
      // Double quotes: "value" (not part of a word)
      highlightedBody = highlightedBody.replace(/(?<=\s|^)"([^"\s][^"]*?)"(?!\w)/g, '<span style="font-weight:bold;font-style:italic;background:#f3f4f6;padding:1px 4px;border-radius:4px;">$1</span>');
      // Parentheses: (value) (not part of a word)
      highlightedBody = highlightedBody.replace(/(?<=\s|^)\(([^\s][^\)]*?)\)(?=\s|[.,;:]|$)/g, '<span style="font-weight:bold;font-style:italic;background:#f3f4f6;padding:1px 4px;border-radius:4px;">($1)</span>');
      // Pipes: |value| (not part of a word)
      highlightedBody = highlightedBody.replace(/(?<=\s|^)\|([^\|\s][^\|]*?)\|(?=\s|[.,;:]|$)/g, '<span style="font-weight:bold;font-style:italic;background:#f3f4f6;padding:1px 4px;border-radius:4px;">$1</span>');
      // Now convert line breaks to <br/>
      highlightedBody = highlightedBody.replace(/\n/g, '<br/>');
      return `${br}<strong>Analysis:</strong><br/>${highlightedBody}`;
    });

    // Format command blocks: ```command``` or "Please run this command:" followed by code
    formatted = formatted.replace(/(Please run this command:)([\s\S]*?)(Then share the output with me\.)/gi, (match, prefix, command, suffix) => {
      const cleanCommand = command.trim().replace(/```/g, '').replace(/`/g, '');
      return `${prefix}<br/><div style="background: #f8f9fa; border: 1px solid #e9ecef; padding: 8px; border-radius: 4px; font-family: 'Courier New', monospace; font-size: 13px; margin: 4px 0; overflow-x: auto;">${cleanCommand}</div><br/>${suffix}`;
    });

    // Format multiple commands mixed with text - NEW ENHANCEMENT
    // Handle cases where there are multiple "Please run this command:" sections in one response
    // This handles the general case of multiple commands with text in between
    
    // First, let's handle the specific case of exactly 2 commands
    formatted = formatted.replace(/(Please run this command:)([\s\S]*?)(Then share the output with me\.)([\s\S]*?)(Please run this command:)([\s\S]*?)(Then share the output with me\.)/gi, (match, prefix1, command1, suffix1, middleText, prefix2, command2, suffix2) => {
      const cleanCommand1 = command1.trim().replace(/```/g, '').replace(/`/g, '');
      const cleanCommand2 = command2.trim().replace(/```/g, '').replace(/`/g, '');
      const formattedMiddleText = middleText.trim().replace(/\n/g, '<br/>');
      
      return `${prefix1}<br/><div style="background: #f8f9fa; border: 1px solid #e9ecef; padding: 8px; border-radius: 4px; font-family: 'Courier New', monospace; font-size: 13px; margin: 4px 0; overflow-x: auto;">${cleanCommand1}</div><br/>${suffix1}<br/><br/>${formattedMiddleText}<br/><br/>${prefix2}<br/><div style="background: #f8f9fa; border: 1px solid #e9ecef; padding: 8px; border-radius: 4px; font-family: 'Courier New', monospace; font-size: 13px; margin: 4px 0; overflow-x: auto;">${cleanCommand2}</div><br/>${suffix2}`;
    });
    
    // Now handle the general case of any number of commands mixed with text
    // Split the text into sections and process each command block
    const commandSections = formatted.split(/(Please run this command:)/gi);
    if (commandSections.length > 1) {
      let processedText = '';
      for (let i = 0; i < commandSections.length; i++) {
        if (i === 0) {
          // First section is just text before any command
          processedText += commandSections[i];
        } else if (i % 2 === 1) {
          // This is a "Please run this command:" section
          const commandText = commandSections[i + 1] || '';
          const commandMatch = commandText.match(/([\s\S]*?)(Then share the output with me\.)/);
          
          if (commandMatch) {
            const command = commandMatch[1].trim().replace(/```/g, '').replace(/`/g, '');
            const suffix = commandMatch[2];
            const remainingText = commandText.substring(commandMatch[0].length);
            
            processedText += `Please run this command:<br/><div style="background: #f8f9fa; border: 1px solid #e9ecef; padding: 8px; border-radius: 4px; font-family: 'Courier New', monospace; font-size: 13px; margin: 4px 0; overflow-x: auto;">${command}</div><br/>${suffix}${remainingText}`;
          } else {
            // Fallback if the pattern doesn't match exactly
            processedText += `Please run this command:${commandText}`;
          }
        }
      }
      formatted = processedText;
    }

    // Format code blocks with backticks
    formatted = formatted.replace(/```([\s\S]*?)```/g, (match, code) => {
      return `<div style="background: #f8f9fa; border: 1px solid #e9ecef; padding: 8px; border-radius: 4px; font-family: 'Courier New', monospace; font-size: 13px; margin: 4px 0; overflow-x: auto;">${code.trim()}</div>`;
    });

    // Add search response prefix if this is a search response
    if (isSearchResponse) {
      formatted = `<div style="display: flex; align-items: center; margin-bottom: 8px; color: #2563eb; font-weight: 600; font-size: 12px;">
        <svg width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" style="margin-right: 6px;">
          <circle cx="11" cy="11" r="8"/>
          <path d="m21 21-4.35-4.35"/>
        </svg>
        SEARCH RESULT
      </div>${formatted}`;
    }

    return formatted;
  };

  // Global keyword detection function
  const handleGlobalKeywords = (message) => {
    const trimmedMessage = message.trim().toLowerCase();
    
    console.log("🔍 Checking global keywords for:", trimmedMessage);
    
    // \\clear — wipe copilot context and TestPilot session memory
    if (trimmedMessage === "\\clear" || trimmedMessage === "/clear") {
      // Clear copilot messages immediately with a placeholder
      setCopilotMessages([]);
      
      // Tell backend to wipe LangGraph memory
      if (socket) {
        socket.emit("clearTestPilotSession", {});
      } else {
        // No socket — just reset locally
        setCopilotMessages([{
          text: "🧹 Context cleared. Starting a fresh conversation!",
          sender: "system",
          timestamp: Date.now()
        }]);
      }
      
      // Clear input
      setMessage("");
      setTimeout(() => {
        const textareas = document.querySelectorAll('textarea');
        textareas.forEach(textarea => {
          textarea.style.height = 'auto';
          textarea.style.height = '28px';
        });
      }, 0);
      
      return true;
    }
    
    // Escalation keywords
    if (trimmedMessage === 'escalate' || trimmedMessage === 'live agent') {
      console.log("[ESCALATION-TRIGGER-DEBUG] ========== ESCALATION KEYWORD DETECTED ==========");
      console.log("[ESCALATION-TRIGGER-DEBUG] Message:", message);
      console.log("[ESCALATION-TRIGGER-DEBUG] Trimmed message:", trimmedMessage);
      console.log("[ESCALATION-TRIGGER-DEBUG] Socket exists:", !!socket);
      
      // Add user message to chat
      setMessages((prev) => [...prev, { text: message, sender: username, timestamp: Date.now() }]);
      
      // Add immediate confirmation message
      setTimeout(() => {
        setMessages((prev) => [...prev, { 
          text: "🔄 Escalating to live agent... Please wait while we connect you to a support representative.", 
          sender: "bot",
          timestamp: Date.now()
        }]);
      }, 100);
      
      // Trigger escalation
      if (socket) {
        console.log("[ESCALATION-TRIGGER-DEBUG] Emitting triggerEscalation event");
        socket.emit("triggerEscalation", { userMessage: message });
        console.log("[ESCALATION-TRIGGER-DEBUG] triggerEscalation event emitted");
      } else {
        console.error("[ESCALATION-TRIGGER-DEBUG] Socket not available for escalation");
      }
      
      // Clear input and reset textarea
      setMessage("");
      setTimeout(() => {
        const textareas = document.querySelectorAll('textarea');
        textareas.forEach(textarea => {
          textarea.style.height = 'auto';
          textarea.style.height = '28px';
        });
      }, 0);
      
      console.log("[ESCALATION-TRIGGER-DEBUG] ========== ESCALATION TRIGGER COMPLETED ==========");
      return true; // Keyword handled
    }
    
    // Session reset keywords
    // For copilot/testpilot mode, let /clear or clear pass through to backend
    // For diagnostic mode, handle locally
    const isClearCommand = trimmedMessage === '/clear' || trimmedMessage === 'clear';
    if (trimmedMessage === 'exit' || trimmedMessage === 'quit' || (isClearCommand && mode !== 'copilot')) {
      // Add user message to chat briefly, then reset
      setMessages((prev) => [...prev, { text: message, sender: username, timestamp: Date.now() }]);
      
      // Add confirmation message briefly
      setTimeout(() => {
        setMessages((prev) => [...prev, { 
          text: "🔄 Resetting session...", 
          sender: "bot",
          timestamp: Date.now()
        }]);
      }, 100);
      
      // Reset all chat state after a short delay
      setTimeout(() => {
        setMessages([]);
        setIsWaitingForCommand(false);
        setIsWaitingForDecision(false);
        setInRecommendation(false);
        setRecommendationStartIdx(-1);
        setSentiment(8);
        console.log("🔄 Setting mode to diagnostic from handleGlobalKeywords");
        setMode("diagnostic");
        
        // Reset avatar panel if open
        if (avatarPanelOpen) {
          setAvatarPanelOpen(false);
          if (avatarRef.current && avatarRef.current.sessionState !== "INACTIVE") {
            avatarRef.current.stopSession();
          }
        }
        
        // Emit session reset to backend
        if (socket) {
          socket.emit("resetSession");
          
          // Re-join room with user information to ensure proper participant tracking
          setTimeout(() => {
            const sessionId = socket.id;
            console.log("calling join room from ResetSession")
            socket.emit("joinRoom", {
              sessionId,
              role: "user",
              name: username || localStorage.getItem("username") || "User",
            });
          }, 100);
        }
      }, 500);
      
      // Clear input and reset textarea immediately
      setMessage("");
      setTimeout(() => {
        const textareas = document.querySelectorAll('textarea');
        textareas.forEach(textarea => {
          textarea.style.height = 'auto';
          textarea.style.height = '28px';
        });
      }, 0);
      
      return true; // Keyword handled
    }
    
    console.log("🔍 No keyword detected, returning false");
    return false; // No keyword detected
  };

  const sendMessage = () => {
    if (!message.trim() || !socket) return;
    
    const trimmedMessage = message.trim().toLowerCase();
    
    // Check for /clear command in copilot mode - handle silently before adding to UI
    if (mode === "copilot" && (trimmedMessage === "/clear" || trimmedMessage === "clear")) {
      console.log("🔄 /clear command detected - sending to backend silently");
      // Send to backend but don't add to chat history
      socket.emit("testpilotMessage", {
        text: message,
        mode: mode || "testpilot",
        username: username || localStorage.getItem("username") || "User",
      });
      // Clear input field
      setMessage("");
      return;
    }
    
    // Check for global keywords FIRST (before adding to UI)
    if (handleGlobalKeywords(message)) {
      return; // Keyword was handled, don't proceed with normal flow
    }
    
    // Add message to appropriate chat history based on mode
    const newMessage = { 
      text: message, 
      sender: username, 
      timestamp: Date.now(),
      isSearchQuery: mode === "search",
      sources: []
    };

    if (mode === "diagnostic") {
      setDiagnosticMessages(prev => [...prev, newMessage]);
      // Show typing indicator immediately for diagnostic mode
      setShowTypingIndicator(true);
    } else if (mode === "search") {
      setSearchMessages(prev => [...prev, newMessage]);
      // Auto-select "Answer" tab when sending a search query
      setSearchSubMode("rag");
    } else if (mode === "copilot") {
      setCopilotMessages(prev => [...prev, newMessage]);
    }
    
    // Handle different modes
    if (mode === "search") {
      // SEARCH MODE: Use RAG system
      console.log("🔍 Sending search query:", message);
      socket.emit("searchQuery", { text: message });
    } else if (mode === "copilot") {
      // COPILOT MODE: Send to backend
      console.log("🤖 Sending user message:", message);
      console.log("🤖 Socket exists:", !!socket);
      
      // ===== COPILOT (OLD) =====
      // socket.emit("userMessage", { text: message });
      // OR
      // socket.emit("copilotMessage", { 
      //   text: message,
      //   room_id: "copilot_room",
      //   target: "copilot"
      // });
      
      // ===== TESTPILOT (NEW) =====
      console.log("🤖 Emitting testpilotMessage event...");
      socket.emit("testpilotMessage", {
        text: message,
        mode: mode || "testpilot",
        username: username || localStorage.getItem("username") || "User",
      });
      console.log("🤖 testpilotMessage event emitted");
    } else {
      // DIAGNOSTIC MODE: Use existing diagnostic flow
      console.log("🩺 Sending diagnostic message:", message);
      
      // Update title only on first message in diagnostic mode
      if (!isTitleSet) {
        const newTitle = getTitleFromMessage(message);
        setSupportType(newTitle);
        setIsTitleSet(true);
      }
      
      // Emit diagnostic message
      socket.emit("chatMessage", { text: message });
    }

    // Clear message and reset UI
    setMessage("");
    setTimeout(() => {
      const textareas = document.querySelectorAll('textarea');
      textareas.forEach(textarea => {
        textarea.style.height = 'auto';
        textarea.style.height = '28px';
      });
    }, 0);
  };

  // Only show landing text until first message
  const chatStarted = messages.length > 0;

  // Check if agent is present in participants
  const agent = participants.find((p) => p.role === "agent");
  const isAgentPresent = !!agent;

  // Add new state for search dropdown
  const [searchDropdownOpen, setSearchDropdownOpen] = useState(false);
  const searchDropdownRef = useRef(null);

  const [previousMode, setPreviousMode] = useState("diagnostic");
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);

  // Search source options
  const [searchSources, setSearchSources] = useState([
    {
      id: 'CaseNotes',
      icon: (
        <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10"/>
          <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
        </svg>
      ),
      title: 'CaseNotes',
      description: 'search for CaseNotes',
      active: true
    },
    {
      id: 'Chat History',
      icon: (
        <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path d="M22 10v6M2 10l10-5 10 5-10 5z"/>
          <path d="M6 12v5c3 3 9 3 12 0v-5"/>
        </svg>
      ),
      title: 'Chat History',
      description: 'Search Chat History',
      active: true
    },
    {
      id: 'Defects',
      icon: (
        <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <circle cx="18" cy="5" r="3"/>
          <circle cx="6" cy="12" r="3"/>
          <circle cx="18" cy="19" r="3"/>
          <path d="M8.59 13.51l6.83 3.98M15.41 6.51l-6.82 3.98"/>
        </svg>
      ),
      title: 'Defects',
      description: 'search for Defects',
      active: true
    },
    {
      id: 'Product Docs',
      icon: (
        <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path d="M21 5H3v14h18V5zM3 10h18"/>
          <path d="M7 15h.01M11 15h.01M15 15h.01"/>
        </svg>
      ),
      title: 'Product Docs',
      description: 'search for Product Docs',
      active: true
    }
  ]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchDropdownRef.current && !searchDropdownRef.current.contains(event.target)) {
        setSearchDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Toggle search source
  const toggleSearchSource = (sourceId) => {
    setSearchSources(sources => 
      sources.map(source => ({
        ...source,
        active: source.id === sourceId ? !source.active : source.active
      }))
    );
  };

  const buildConversationSearchContext = () => {
    const conversationMessages = [...diagnosticMessages, ...copilotMessages]
      .filter((msg) => msg?.text && msg.sender !== "system")
      .slice(-30);

    return conversationMessages
      .map((msg) => {
        const speaker = msg.sender === username ? "User" : (msg.sender_name || msg.sender || "Assistant");
        return `${speaker}: ${msg.text}`;
      })
      .join("\n\n");
  };

  const openSearchWindow = () => {
    const conversationContext = buildConversationSearchContext();
    const contextKey = `arta-search-context-${Date.now()}`;

    if (conversationContext) {
      sessionStorage.setItem(contextKey, conversationContext);
    }

    const searchUrl = new URL("/search", window.location.origin);
    if (conversationContext) {
      searchUrl.searchParams.set("conversationKey", contextKey);
    }

    const opened = window.open(
      searchUrl.toString(),
      "_blank",
      "width=1200,height=800,scrollbars=yes,resizable=yes"
    );

    if (!opened) {
      navigate(searchUrl.pathname + searchUrl.search);
    }
  };

  // Helper to capitalize names
  const capitalize = (str) => {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  // Check if scroll indicator should be shown
  const checkScrollIndicator = () => {
    if (mode !== "search" || !chatBodyRef.current) return;
    
    const container = chatBodyRef.current;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;
    const scrollTop = container.scrollTop;
    
    // Show indicator if there's more content below the visible area
    const hasMoreContent = scrollHeight > clientHeight && scrollTop < (scrollHeight - clientHeight - 50);
    setShowScrollIndicator(hasMoreContent);
  };

  // Handle scroll events
  const handleScroll = () => {
    checkScrollIndicator();
  };



  // Add new state for tab hover
  const [hoveredTab, setHoveredTab] = useState(null);



  // Utility Bar JSX for reuse
  const utilityBar = (
    <div className="mt-2 w-full max-w-3xl mx-auto flex justify-center">
      <div className="flex items-center gap-3 w-full px-2 py-1">
        {/* Mode Toggle Buttons */}
        <div className="flex items-center bg-gray-100 rounded-lg p-1">
          <button
            className={`px-3 py-1 rounded-md text-xs font-medium transition ${
              mode === "diagnostic"
                ? "bg-[#06b6d4] text-white"
                : "text-gray-600 hover:text-gray-900"
            }`}
            onClick={() => setMode("diagnostic")}
            type="button"
          >
            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" className="inline mr-1">
              <path d="M9 12l2 2 4-4"/>
              <path d="M12 2v20M2 12h20"/>
            </svg>
            vTechSupport
          </button>
          <button
            className={`px-3 py-1 rounded-md text-xs font-medium transition ${
              mode === "copilot"
                ? "bg-[#06b6d4] text-white"
                : "text-gray-600 hover:text-gray-900"
            }`}
            onClick={() => {
              console.log("🤖 Copilot mode button clicked, current mode:", mode);
              setMode("copilot");
              console.log("🤖 Mode set to copilot");
              
              // Notify TSE agents that a user has entered copilot mode
              if (socket) {
                socket.emit("userEnteredCopilot", {
                  user_sid: socket.id,
                  username: username || localStorage.getItem("username") || "User"
                });
                console.log("🤖 Notified TSE agents about copilot session");
              }
            }}
            type="button"
          >
            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" className="inline mr-1">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
            </svg>
            Copilot
          </button>
          <button
            className={`px-3 py-1 rounded-md text-xs font-medium transition ${
              mode === "search"
                ? "bg-[#06b6d4] text-white"
                : "text-gray-600 hover:text-gray-900"
            }`}
            onClick={openSearchWindow}
            type="button"
          >
            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" className="inline mr-1">
              <circle cx="11" cy="11" r="8"/>
              <path d="m21 21-4.35-4.35"/>
            </svg>
            Search
          </button>
        </div>

        {/* Session Reset Button (only show in diagnostic mode with active session) */}
        {mode === "diagnostic" && sessionStatus.hasActiveSession && (
          <button 
            title="Reset Diagnostic Session"
            className="flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition"
            onClick={() => {
                                      // eslint-disable-next-line no-restricted-globals
              if (confirm("🔄 Reset diagnostic session? This will clear all troubleshooting progress.")) {
                socket?.emit("resetSession");
                setMessages(prev => [...prev, { 
                  text: "🔄 Diagnostic session reset. Starting fresh...", 
                  sender: "system", 
                  timestamp: Date.now() 
                }]);
                
                // Clear diagnostic state
                setLastDiagnosticState(null);
              }
            }}
            type="button"
          >
            <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
              <path d="M3 3v5h5"/>
            </svg>
            Reset
          </button>
        )}

        {/* Sources Button - Show when sources are available AND in diagnostic mode */}
        {diagnosticSources.length > 0 && mode === "diagnostic" && (
          <button
            title={`View Sources (${diagnosticSources.length})`}
            className={`flex items-center gap-2 px-3 py-1 rounded-lg transition ${
              sourcesPanelOpen && sidebarMode === "sources"
                ? "bg-[#06b6d4] text-white" 
                : "bg-gray-100 text-gray-600 hover:text-gray-900"
            }`}
            onClick={() => {
              setSidebarMode("sources");
              setSourcesPanelOpen(!sourcesPanelOpen);
            }}
            type="button"
          >
            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
              <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
            </svg>
            <span className="text-xs font-medium">Sources</span>
            <span className="bg-white bg-opacity-30 text-xs px-1.5 py-0.5 rounded-full min-w-[16px] text-center">
              {diagnosticSources.length}
            </span>
          </button>
        )}

        {/* Set Sources for Search Button */}
        <button
          title="Set Sources for Search"
          className={`hover:bg-[#E6F0EF] p-2 rounded-lg transition ${
            sourcesPanelOpen && sidebarMode === "search" ? "bg-[#E6F0EF] ring-2 ring-[#06b6d4]" : ""
          }`}
          onClick={() => {
            setSidebarMode("search");
            setSourcesPanelOpen(true);
          }}
          type="button"
        >
          <svg width="20" height="20" fill="none" stroke="#253439" strokeWidth="2" viewBox="0 0 24 24">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
            <polyline points="10 9 9 9 8 9" />
          </svg>
        </button>

        {/* Attach Files */}
        <button title="Attach Files" className="hover:bg-[#E6F0EF] p-2 rounded-lg transition" type="button">
          <svg width="20" height="20" fill="none" stroke="#253439" strokeWidth="2" viewBox="0 0 24 24">
            <path d="M21.44 11.05a5.5 5.5 0 0 1-7.78 7.78l-8.49-8.49a4 4 0 0 1 5.66-5.66l7.78 7.78" />
          </svg>
        </button>
        {/* Dictation (Mic) */}
        <button title="Dictation" className="hover:bg-[#E6F0EF] p-2 rounded-lg transition" type="button">
          <svg width="20" height="20" fill="none" stroke="#253439" strokeWidth="2" viewBox="0 0 24 24">
            <rect x="9" y="2" width="6" height="12" rx="3" />
            <path d="M5 10v2a7 7 0 0 0 14 0v-2" />
            <path d="M12 19v3m-4 0h8" />
          </svg>
        </button>
        {/* Voice Mode */}
        <button title="Voice Mode" className="hover:bg-[#E6F0EF] p-2 rounded-lg transition" type="button">
          <svg width="20" height="20" fill="none" stroke="#253439" strokeWidth="2" viewBox="0 0 24 24">
            <path d="M9 9v6l7-3-7-3z" />
            <circle cx="12" cy="12" r="10" />
          </svg>
        </button>
        {/* AI Agent Video Mode */}
        <button
          title="AI Agent Video"
          className={`hover:bg-[#E6F0EF] p-2 rounded-lg transition ${
            avatarPanelOpen ? 'bg-[#E6F0EF] ring-2 ring-[#06b6d4]' : ''
          }`}
          onClick={() => {
            const newPanelOpen = !avatarPanelOpen;
            setAvatarPanelOpen(newPanelOpen);
            console.log("🎭 Avatar panel toggled to:", newPanelOpen);
            
            if (newPanelOpen) {
              console.log("🎭 Opening avatar panel - auto-clicking Start Avatar button");
              setTimeout(() => {
                if (avatarRef.current && avatarRef.current.clickStartButton) {
                  avatarRef.current.clickStartButton();
                }
              }, 100);
            } else {
              console.log("🎭 Closing avatar panel - stopping session");
              if (avatarRef.current && avatarRef.current.sessionState !== "INACTIVE") {
                avatarRef.current.stopSession();
              }
            }
          }}
          type="button"
        >
          <img 
            src="/avatar_video_png.png" 
            alt="AI Agent Video" 
            width="20" 
            height="20" 
          />
        </button>
        {/* Dynamic Sentiment Speedometer */}
        <div className="flex-1"></div>
        <div className="flex items-center ml-auto">
          <Speedometer score={sentiment} size={60} />
        </div>
      </div>
    </div>
  );

  // Enterprise variant: the same utility icons, rendered inline inside the
  // input row (before the Send button) instead of in a separate bottom bar.
  const enterpriseToolbarIcons = (
    <div className="flex items-center gap-0.5 mr-1">
      {/* Dictation (Mic) */}
      <button title="Dictation" className="hover:bg-[#E6F0EF] p-2 rounded-lg transition" type="button">
        <svg width="20" height="20" fill="none" stroke="#253439" strokeWidth="2" viewBox="0 0 24 24">
          <rect x="9" y="2" width="6" height="12" rx="3" />
          <path d="M5 10v2a7 7 0 0 0 14 0v-2" />
          <path d="M12 19v3m-4 0h8" />
        </svg>
      </button>
      {/* Voice Mode */}
      <button title="Voice Mode" className="hover:bg-[#E6F0EF] p-2 rounded-lg transition" type="button">
        <svg width="20" height="20" fill="none" stroke="#253439" strokeWidth="2" viewBox="0 0 24 24">
          <path d="M9 9v6l7-3-7-3z" />
          <circle cx="12" cy="12" r="10" />
        </svg>
      </button>
      {/* AI Agent Video Mode */}
      <button
        title="AI Agent Video"
        className={`hover:bg-[#E6F0EF] p-2 rounded-lg transition ${
          avatarPanelOpen ? "bg-[#E6F0EF] ring-2 ring-[#06b6d4]" : ""
        }`}
        onClick={() => {
          const newPanelOpen = !avatarPanelOpen;
          setAvatarPanelOpen(newPanelOpen);
          if (newPanelOpen) {
            setTimeout(() => {
              if (avatarRef.current && avatarRef.current.clickStartButton) {
                avatarRef.current.clickStartButton();
              }
            }, 100);
          } else {
            if (avatarRef.current && avatarRef.current.sessionState !== "INACTIVE") {
              avatarRef.current.stopSession();
            }
          }
        }}
        type="button"
      >
        <img src="/avatar_video_png.png" alt="AI Agent Video" width="20" height="20" />
      </button>
    </div>
  );

  // Chat input box (textarea + action buttons). Reused in two positions:
  // pinned to the bottom (default view) or centered under the greeting
  // (enterprise view).
  const chatInputBox = (
    <div className={`bg-white rounded-xl border border-[#06b6d4] shadow-md flex flex-col px-6 py-4`}>
      {/* Attachment previews */}
      {isEnterprise && attachments.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {attachments.map((att, idx) => (
            <div key={idx} className="relative group">
              {att.preview ? (
                <img src={att.preview} alt={att.file.name} className="h-16 w-16 object-cover rounded-lg border border-gray-200" />
              ) : (
                <div className="h-16 w-16 flex items-center justify-center bg-gray-100 rounded-lg border border-gray-200">
                  <FileText size={24} className="text-gray-400" />
                </div>
              )}
              <button
                type="button"
                onClick={() => removeAttachment(idx)}
                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition"
              >
                <X size={12} />
              </button>
              <div className="text-[10px] text-gray-500 truncate max-w-[64px] text-center">{att.file.name}</div>
            </div>
          ))}
        </div>
      )}
      {attachError && <div className="text-xs text-red-500 mb-2">{attachError}</div>}
      <div className={`flex ${isEnterprise ? "items-end" : "items-start"}`}>
      {/* Attach button with menu */}
      {isEnterprise && (
        <div className="relative mr-2 flex-shrink-0" ref={attachMenuWrapRef}>
          <button
            title={`Attach files (${SUPPORTED_FORMATS_LABEL})`}
            className="hover:bg-[#E6F0EF] p-2 rounded-lg transition text-[#253439] flex items-center justify-center"
            type="button"
            disabled={attachments.length >= MAX_ATTACHMENTS}
            onClick={() => setAttachMenuOpen((v) => !v)}
          >
            <Plus size={20} />
          </button>
          <div className={`absolute bottom-full left-0 mb-2 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden transition-all ${attachMenuOpen ? "opacity-100 visible" : "opacity-0 invisible"}`} style={{ minWidth: "200px" }}>
            <button
              type="button"
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 text-left"
              onClick={() => { setAttachMenuOpen(false); imageInputRef.current?.click(); }}
            >
              <ImageIcon size={18} className="text-cyan-600" />
              <div>
                <div className="text-sm font-medium text-gray-700">Add images</div>
                <div className="text-xs text-gray-400">{IMAGE_FORMATS_LABEL}</div>
              </div>
            </button>
            <button
              type="button"
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 text-left border-t border-gray-100"
              onClick={() => { setAttachMenuOpen(false); documentInputRef.current?.click(); }}
            >
              <Paperclip size={18} className="text-cyan-600" />
              <div>
                <div className="text-sm font-medium text-gray-700">Add files</div>
                <div className="text-xs text-gray-400">{DOCUMENT_STABLE_LABEL}</div>
                {DOCUMENT_BETA_LABEL && (
                  <div className="text-xs text-gray-400">
                    <span className="inline-block bg-amber-100 text-amber-700 text-[10px] px-1 rounded mr-1">Beta</span>
                    {DOCUMENT_BETA_LABEL}
                  </div>
                )}
              </div>
            </button>
          </div>
          <input
            ref={imageInputRef}
            type="file"
            accept={ACCEPT_IMAGES}
            multiple
            className="hidden"
            onChange={(e) => { handleFilesSelected(e.target.files, "image"); e.target.value = ""; }}
          />
          <input
            ref={documentInputRef}
            type="file"
            accept={ACCEPT_DOCUMENTS}
            multiple
            className="hidden"
            onChange={(e) => { handleFilesSelected(e.target.files, "document"); e.target.value = ""; }}
          />
        </div>
      )}
      <textarea
        className="flex-1 bg-transparent border-none outline-none text-[#253439] placeholder-[#A2A9AF] focus:ring-0 resize-none overflow-y-auto"
        style={{
          minHeight: '28px',
          maxHeight: '200px',
          height: '28px',
          fontFamily: "'Space Grotesk', ui-sans-serif, system-ui, -apple-system, 'system-ui', 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif",
          fontSize: "14px"
        }}
        placeholder={
          mode === "search"
            ? "🔍 Search knowledge base..."
            : isWaitingForCommand
            ? "Enter command output…"
            : isWaitingForDecision
            ? "Type yes or no…"
            : "Ask follow-up, or type a new question…"
        }
        value={message}
        onChange={(e) => {
          setMessage(e.target.value);
          autoResizeTextarea(e.target);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            if (isEnterprise) sendEnterpriseQuery();
            else sendMessage();
          }
        }}
      />
      {/* Video Button - Only show when agent is present */}
      {isAgentPresent && (
        <button
          className="ml-2 p-2 rounded-full text-[#06b6d4] hover:bg-[#E6F0EF] transition"
          aria-label="Video Chat"
          onClick={videoOpen ? handleStopVideo : handleStartVideo}
          type="button"
        >
          <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <rect x="2" y="6" width="14" height="12" rx="2" />
            <path d="m16 13 5.223 3.482a.5.5 0 0 0 .777-.416V7.87a.5.5 0 0 0-.752-.432L16 10.5"/>
          </svg>
        </button>
      )}
      {/* Enterprise view: utility icons inline, before the Send button */}
      {isEnterprise && enterpriseToolbarIcons}
      {/* Send Button */}
      <button
        className="ml-2 bg-[#06b6d4] hover:bg-cyan-700 rounded-lg p-2 transition"
        aria-label="Send"
        onClick={isEnterprise ? sendEnterpriseQuery : sendMessage}
        type="button"
      >
        <svg width="20" height="20" fill="none" stroke="white" strokeWidth="2" viewBox="0 0 24 24">
          <path d="M22 2 11 13" />
          <path d="M22 2l-7 20-4-9-9-4z" />
        </svg>
      </button>
      </div>{/* close inner flex row */}
    </div>
  );

  // Enterprise split view: top query input + two result panes
  // (left: TSEDashboard2 "AI Mode", right: TSEAgentRag "Search Mode").
  const enterpriseSplitView = (
    <TseDashboard5
      // Remount only when the chat actually changes — see entMountKeyRef.
      key={entMountKeyRef.current}
      embedded
      // Present = reopen that saved chat; absent = start one. The dashboard's
      // embedded branch reads this before falling back to sessionStorage.
      sessionId={routeSessionId || null}
      isNewSession={!entResumedMountRef.current}
      onSessionIdAssigned={handleEntSessionIdAssigned}
      // How the ghost above reaches this chat.
      sendRef={entSendRef}
      variant="enterprise"
      username={username}
      agentNameOverride={getCurrentAgentName()}
      onLogout={onLogout}
      sentiment={sentiment}
      prevSentiment={prevSentiment}
      tseAgentName={tseAgentName}
      onSentimentUpdate={(data) => {
        if (data && typeof data.sentiment === "number") {
          setSentiment(current => {
            setPrevSentiment(current);
            return data.sentiment;
          });
        }
      }}
    />
  );

  const handleSidebarClick = (path) => {
    navigate(path);
  };

  // "New chat" in the enterprise rail. Clearing the view here as well as
  // navigating is what makes it instant: the effect above reaches the same
  // state off the address change, but only after the router re-renders.
  const handleEntNewChat = () => {
    setSplitView(false);
    setSplitQuery("");
    setMessage("");
    navigate(ENT_BASE_PATH);
  };

  useEffect(() => {
  console.log("🔍 searchSourcesMap updated:", searchSourcesMap);
}, [searchSourcesMap]);

  // Set up scroll event listener and check scroll indicator
  useEffect(() => {
    const container = chatBodyRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [mode]);

  // Check scroll indicator when search messages change
  useEffect(() => {
    if (mode === "search") {
      // Small delay to ensure DOM is updated
      setTimeout(checkScrollIndicator, 100);
    }
  }, [searchMessages, mode]);

  return (
    <div className={`flex w-full transition-colors duration-300 bg-[#F3F3ED] ${isEnterprise ? "h-screen overflow-hidden" : "h-full"}`}>
      {/* Add typewriter effect CSS */}
      <style dangerouslySetInnerHTML={{
        __html: `
          .typewriter-effect {
            overflow: hidden;
            white-space: nowrap;
            animation: typing 2s steps(40, end);
          }

          @keyframes typing {
            from { width: 0 }
            to { width: 100% }
          }

          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(8px); }
            to { opacity: 1; transform: translateY(0); }
          }

          .citation-hover-wrap:hover .citation-popover {
            display: block !important;
          }
          .citation-hover-wrap::after {
            content: '';
            position: absolute;
            bottom: 100%;
            left: -4px;
            right: -4px;
            height: 14px;
          }

        `
      }} />
      <SideBar
        mode="user"
        onLogout={onLogout}
        onLinkClick={handleSidebarClick}
        // Recents only belong to the enterprise copilot — the plain user chat
        // has no sessions of its own to list, so its rail stays the 72px
        // logo-and-logout one it has always been.
        showRecents={isEnterprise}
        onNewChat={handleEntNewChat}
        onExpandedChange={setRailExpanded}
      />
      
      {/* Chat Title - Move to top left, above the white background.
          Hidden in the enterprise split view (rendered inline in its header). */}
      {!(isEnterprise && splitView) && (
      <div className={`fixed top-3 z-[100] ${railExpanded ? "left-[256px]" : "left-24"}`}>
        <h1 className={`text-sm font-bold text-[#253439] tracking-wide uppercase ${isTyping ? 'after:content-["|"] after:ml-1 after:animate-pulse' : ''}`}
          style={{ letterSpacing: '0.05em' }}>
          {displayTitle}
        </h1>
      </div>
      )}

      {/* Move user count to the white background with higher z-index.
          Hidden in the enterprise split view (profile is inline in its header). */}
      {!(isEnterprise && splitView) && (
      <div className="fixed top-3 right-8 z-[150] flex items-center gap-2">
        {/* AI Agent Avatar (Emma, Mike, etc.) with name and sentiment dropdown - shown only after first prompt */}
        {chatStarted && (() => {
          const bot = participants.find((p) => p.role === "bot");
          const botName = bot?.name || "AI Agent";
          return (
            <div className="relative" ref={aiAgentMenuRef}>
              <button
                type="button"
                onClick={() => setAiAgentMenuOpen(!aiAgentMenuOpen)}
                className={`d5-avatar-btn d5-avatar-ai ${escalationActive ? "d5-avatar-disabled" : ""}`}
                title={`${botName} (AI Agent)`}
              >
                {botName.charAt(0).toUpperCase()}
              </button>
              {aiAgentMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-xl shadow-lg py-3 px-4 z-50 animate-fade-in">
                  <div className="text-sm font-semibold text-gray-800">{botName}</div>
                  <div className="text-xs text-gray-500 mt-1">AI Assistant</div>
                  <div className="text-xs font-semibold text-gray-900 mt-2">
                    Sentiment: {sentiment.toFixed(1)}
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* TSE Agent Avatar (Chris) - visible only when escalated */}
        {escalationActive && (
          <button 
            type="button"
            className="d5-avatar-btn d5-avatar-tse"
            title={`${tseAgentName || "Support Engineer"} (Support Engineer)`}
          >
            {(tseAgentName || "Agent").charAt(0).toUpperCase()}
          </button>
        )}

        {/* User Profile Avatar - Burgundy Red in enterprise view */}
        <UserProfileMenu onLogout={onLogout} variant={isEnterprise ? "enterprise" : "default"} sentiment={sentiment} prevSentiment={prevSentiment} />
      </div>
      )}

      {/* Main content area with Perplexity-like rounded border */}
      <div className="flex-1 flex flex-col relative transition-all duration-300 ease-in-out p-1">
        {/* The real copilot page. On the enterprise surface it is mounted from
            the first paint — connected, with its own session — exactly as it is
            on /TseDashboard5. Nothing about it is enterprise-specific except
            the variant. */}
        {isEnterprise && enterpriseSplitView}

        {/* THE GHOST. The greeting screen, unchanged, painted OVER the
            dashboard instead of standing in for it. It collects the first
            message, hands it to the dashboard underneath (entSendRef), and
            disappears — so the enterprise chat starts the same way a copilot
            chat does: one send into a page that was already there.
            `contents` makes this wrapper invisible to layout on the default
            surface, which keeps that view byte-identical. */}
        {!(isEnterprise && splitView) && (
        <div className={isEnterprise
          ? "absolute inset-0 z-40 flex flex-col p-1 bg-[#F3F3ED]"
          : "contents"}>
        {/* Rounded container with border - made much larger */}
        <div className="flex-1 bg-white rounded-2xl border-2 border-[#F3F3ED] shadow-sm overflow-hidden relative">
          {/* === CHAT SCROLL AREA === */}
          <div className="flex flex-col flex-1 items-center w-full h-full">
            {/* Fade line at top for messages */}
            <div className="absolute top-0 left-0 right-0 h-4 bg-gradient-to-b from-white to-transparent z-20 rounded-t-2xl"></div>

            {/* Scroll Indicator - Only show in search mode when there's more content */}
            {showScrollIndicator && mode === "search" && (
              <div className="absolute bottom-24 right-40 z-50">
                <div className="bg-[#06b6d4] rounded-full shadow-lg border border-[#06b6d4] p-2 animate-bounce">
                  <svg width="16" height="16" fill="none" stroke="white" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M7 13l5 5 5-5"/>
                    <path d="M7 6l5 5 5-5"/>
                  </svg>
                </div>
              </div>
            )}



            {/* Main content area */}
            <main
              ref={chatBodyRef}
              className="w-full max-w-4xl flex-1 overflow-y-auto flex flex-col px-6 py-6 hide-scrollbar"
              onClick={(e) => {
                const imageLink = e.target.closest("a.image-link");
                if (!imageLink) return;

                e.preventDefault();
                e.stopPropagation();

                const imageUrl = imageLink.getAttribute("data-image-url");
                if (imageUrl) {
                  handleImageClick(imageUrl);
                }
              }}
              style={{
                minHeight: "400px",
                maxHeight: "calc(100vh - 140px)", // Even more available space
                background: "#FFFFFF",
                marginTop: "16px", // Consistent top margin for both modes
                marginBottom: "10px", // Much smaller bottom margin for more space
                position: "relative",
                zIndex: 10
              }}
            >
              <div className={`w-full ${isEnterprise ? "my-auto" : ""}`}>
                                {mode === "search" ? (
                  /* Search Mode - Perplexity-like UI with Multiple Queries */
                  <div className="w-full space-y-8">
                    {/* Empty State for Search */}
                    {searchMessages.length === 0 && (
                      <div className="flex flex-col items-center justify-center h-96 text-gray-500">
                        <svg width="64" height="64" fill="none" stroke="currentColor" strokeWidth="1" viewBox="0 0 24 24" className="mb-4">
                          <circle cx="11" cy="11" r="8"/>
                          <path d="m21 21-4.35-4.35"/>
                        </svg>
                        <p className="text-lg font-medium mb-2">Search the knowledge base</p>
                        <p className="text-sm text-center max-w-md">
                          Ask any question and get answers from our comprehensive knowledge base with cited sources.
                        </p>
                      </div>
                    )}

                    {/* Process search messages to group queries with their responses */}
                    {(() => {
                      const searchGroups = [];
                      let currentGroup = null;
                      
                      searchMessages.forEach((msg, idx) => {
                        if (msg.sender === username) {
                          // Start new group with user query
                          if (currentGroup) {
                            searchGroups.push(currentGroup);
                          }
                          currentGroup = {
                            query: msg,
                            responses: [],
                            sources: []
                          };
                        } else if (currentGroup) {
                          // Add response to current group
                          currentGroup.responses.push(msg);
                              if (msg.sources?.length > 0) {
                                currentGroup.sources = msg.sources;
                              }
                        }
                      });
                      
                      // Add the last group if it exists
                      if (currentGroup) {
                        searchGroups.push(currentGroup);
                      }
                      
                      return searchGroups.map((group, groupIdx) => (
                        <div key={groupIdx} className="w-full border-b border-gray-100 pb-8 last:border-b-0">
                          {/* Search Query Display with Typewriter Effect */}
                          <div className="mb-6">
                            <h2 className={`text-2xl font-semibold text-[#253439] mb-2 user-query ${groupIdx === searchGroups.length - 1 ? 'typewriter-effect' : ''}`}>
                              {group.query.text}
                            </h2>
                            
                            {/* Dynamic Search Subtabs */}
                            <div className="flex items-center mb-4 border-b border-gray-200">
                              {/* Answer Tab */}
                              <button
                                onClick={() => setSearchSubMode("rag")}
                                className={`px-4 py-2 text-sm font-medium transition ${
                                  searchSubMode === "rag"
                                    ? "border-b-2 border-[#06b6d4] text-[#06b6d4]"
                                    : "text-gray-500 hover:text-[#253439]"
                                }`}
                              >
                                Summary
                              </button>
                              
                              {/* Sources Tab (All Sources) */}
                              <button
                                onClick={() => setSearchSubMode("sources")}
                                className={`px-4 py-2 text-sm font-medium transition ${
                                  searchSubMode === "sources"
                                    ? "border-b-2 border-[#06b6d4] text-[#06b6d4]"
                                    : "text-gray-500 hover:text-[#253439]"
                                }`}
                              >
                                Sources ({group.sources?.length || 0})
                              </button>

                              {/* Dynamic Category Tabs */}
                              {(() => {
                                if (!group.sources?.length) return null;
                                
                                // Group sources by category
                                const sourcesByCategory = group.sources.reduce((acc, source) => {
                                  const category = source.category || "Product Documents";
                                  if (!acc[category]) acc[category] = [];
                                  acc[category].push(source);
                                  return acc;
                                }, {});

                                const categories = Object.keys(sourcesByCategory);
                                
                                return categories.map((category) => {
                                  const colors = getCategoryColor(category);
                                  const isActive = searchSubMode === category;
                                  return (
                                    <button
                                      key={category}
                                      onClick={() => setSearchSubMode(category)}
                                      className={`px-4 py-2 text-sm font-medium transition rounded-lg mx-1 ${colors.bg} ${colors.text} ${colors.border} ${colors.hover} ${isActive ? 'ring-2 ring-[#06b6d4]' : ''}`}
                                      style={{ minWidth: 110 }}
                                    >
                                      {categoryDisplayNames[category] || category} ({sourcesByCategory[category].length})
                                    </button>
                                  );
                                });
                              })()}
                            </div>

                            {/* Sources Placeholder Blocks - Only show for Answer tab */}
                          {searchSubMode === "rag" && (
                          <div className="mb-6">
                            <div className="flex flex-wrap gap-3 mb-4">
                              {group.sources?.length > 0 &&
                                group.sources.slice(0, 4)
                                  .map((source, i) => (
                                    <a
                                      key={i}
                                      href={source.link}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="flex items-center bg-gray-50 rounded-lg px-3 py-2 text-xs text-gray-600 border hover:bg-gray-100 transition max-w-[160px]"
                                    >
                                      <div className={`w-6 h-6 rounded-lg flex items-center justify-center mr-2 flex-shrink-0 ${getCategoryColor(getCategoryFromDocumentName(source.name)).bg}`}>
                                        {(() => {
                                          const categoryColors = getCategoryColor(getCategoryFromDocumentName(source.name));
                                          return source.icon === "pdf" ? (
                                            <svg
                                              width="12"
                                              height="12"
                                              fill="none"
                                              stroke="currentColor"
                                              strokeWidth="2"
                                              viewBox="0 0 24 24"
                                              className={categoryColors.iconColor}
                                            >
                                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                              <polyline points="14,2 14,8 20,8" />
                                              <line x1="16" y1="13" x2="8" y2="13" />
                                              <line x1="16" y1="17" x2="8" y2="17" />
                                              <polyline points="10,9 9,9 8,9" />
                                            </svg>
                                          ) : (
                                            <div className={`w-3 h-3 ${categoryColors.bg.replace('bg-', 'bg-').replace('-100', '-300')} rounded`}></div>
                                          );
                                        })()}
                                      </div>
                                      <span className="truncate">{source.name || "PDF"}</span>
                                    </a>
                                  ))}
                            </div>
                          </div>
                        )}

                          {/* Sources Content Display */}
                          <div className="max-w-full overflow-hidden">
                          {(() => {
                            // Helper function to render sources
                            const renderSources = (sourcesToRender, showCategoryBadge = true, showTopSourceSummary = false) => (
                              <div className="space-y-3 w-full max-w-full">
                                {sourcesToRender.map((source, i) => (
                                  <div key={i} className="w-full max-w-full">
                                    <a
                                      href={source.link}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="flex items-start p-3 bg-gray-50 rounded-lg border hover:bg-gray-100 transition w-full max-w-full"
                                    >
                                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center mr-3 flex-shrink-0 ${getCategoryColor(getCategoryFromDocumentName(source.name)).bg}`}>
                                        {(() => {
                                          const categoryColors = getCategoryColor(getCategoryFromDocumentName(source.name));
                                          return source.icon === "pdf" ? (
                                            <svg
                                              width="14"
                                              height="14"
                                              fill="none"
                                              stroke="currentColor"
                                              strokeWidth="2"
                                              viewBox="0 0 24 24"
                                              className={categoryColors.iconColor}
                                            >
                                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                              <polyline points="14,2 14,8 20,8" />
                                              <line x1="16" y1="13" x2="8" y2="13" />
                                              <line x1="16" y1="17" x2="8" y2="17" />
                                              <polyline points="10,9 9,9 8,9" />
                                            </svg>
                                          ) : (
                                            <div className={`w-4 h-4 ${categoryColors.bg.replace('bg-', 'bg-').replace('-100', '-300')} rounded`}></div>
                                          );
                                        })()}
                                      </div>
                                      <div className="flex-1 min-w-0 overflow-hidden">
                                        <h3 className="text-sm font-medium text-gray-900 truncate">
                                          {source.name || "PDF"}
                                        </h3>
                                        <p className="text-xs text-gray-500 mt-1 line-clamp-2 break-words">
                                          {source.summary || "No summary available"}
                                        </p>
                                        {(showCategoryBadge || source.causal) && (
                                          <div className="mt-2 flex items-center flex-wrap gap-1">
                                            {showCategoryBadge && (
                                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                {source.category || "Product Documents"}
                                              </span>
                                            )}
                                            {source.causal && (
                                              <span
                                                className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800"
                                                title="Has diagnostic knowledge graph"
                                              >
                                                <svg
                                                  width="10"
                                                  height="10"
                                                  fill="none"
                                                  stroke="currentColor"
                                                  strokeWidth="2"
                                                  viewBox="0 0 24 24"
                                                >
                                                  <rect x="3" y="3" width="7" height="7" rx="1" />
                                                  <rect x="14" y="14" width="7" height="7" rx="1" />
                                                  <path d="M6.5 10v4a2 2 0 0 0 2 2H14" />
                                                </svg>
                                                Runbook
                                              </span>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    </a>
                                    
                                    {/* AI-Generated Summary for Top Source in Category Tabs */}
                                    {showTopSourceSummary && i === 0 && (() => {
                                      // Extract document filename from link
                                      const documentPath = source.link?.split('/').pop();
                                      const aiSummary = documentSummaries[documentPath];
                                      
                                      // Request summary if not already available
                                      if (!aiSummary && documentPath) {
                                        requestDocumentSummary(documentPath, group.query.text);
                                      }
                                      
                                      return (
                                        <div className="mt-3 p-3 bg-blue-50 rounded-lg border-l-4 border-blue-400">
                                          <div className="flex items-start">
                                            <div className="flex-shrink-0">
                                              <svg className="h-4 w-4 text-blue-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                              </svg>
                                            </div>
                                            <div className="ml-2 flex-1">
                                              <h4 className="text-sm font-medium text-blue-900">AI Summary</h4>
                                              <div className="mt-1 text-sm text-blue-800">
                                                {aiSummary ? (
                                                  <p className="leading-relaxed">{aiSummary}</p>
                                                ) : (
                                                  <div className="flex items-center space-x-2">
                                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                                    <span className="text-xs text-blue-600">Generating summary...</span>
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })()}
                                  </div>
                                ))}
                              </div>
                            );

                            // Handle different tab modes
                            if (searchSubMode === "sources") {
                              // Show all sources
                              return (
                                <div className="mb-6">
                                  {group.sources?.length > 0 ? (
                                    renderSources(group.sources, true)
                                  ) : (
                                    <div className="text-center py-8 text-gray-500">
                                      <svg
                                        width="48"
                                        height="48"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="1"
                                        viewBox="0 0 24 24"
                                        className="mx-auto mb-3"
                                      >
                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                        <polyline points="14,2 14,8 20,8" />
                                      </svg>
                                      <p>No sources available yet</p>
                                      <p className="text-xs mt-1">Sources will appear after you search</p>
                                    </div>
                                  )}
                                </div>
                              );
                            } else if (searchSubMode !== "rag" && group.sources?.length > 0) {
                              // Show category-specific sources
                              const categoryName = searchSubMode;
                              const categorySources = group.sources.filter(
                                source => (source.category || "Product Documents") === categoryName
                              );

                              return (
                                <div className="mb-6">
                                  {categorySources.length > 0 && (
                                    <div>
                                      {/* Top Source with AI Summary (unchanged) */}
                                      <div className="mb-6 p-5 bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl border border-blue-100 shadow-sm max-w-[85%] self-start overflow-hidden">
                                        <div className="flex items-center mb-3">
                                          <h4 className="text-lg font-semibold text-gray-900 mr-2">Overview</h4>
                                          <div className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-medium">Generated</div>
                                        </div>
                                        <div className="text-gray-700 leading-relaxed">
                                          {(() => {
                                            const firstSource = categorySources[0];
                                            const documentPath = firstSource.link?.split('/').pop();
                                            console.log("🔍 Document path extraction:", { 
                                              originalLink: firstSource.link, 
                                              extractedPath: documentPath,
                                              hasSummary: !!documentSummaries[documentPath]
                                            });
                                            const aiSummary = documentSummaries[documentPath];
                                            if (!aiSummary && documentPath) {
                                              console.log("📄 Requesting summary for document path:", documentPath);
                                              requestDocumentSummary(documentPath, group.query.text);
                                            }
                                            return aiSummary ? <div className="prose prose-sm max-w-none"><p>{aiSummary}</p></div> : (
                                              <div className="flex items-center space-x-3">
                                                <div className="flex space-x-1">
                                                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></div>
                                                  <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                                                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                                                </div>
                                                <span className="text-sm text-gray-600">Generating overview...</span>
                                              </div>
                                            );
                                          })()}
                                        </div>
                                      </div>
                                      {/* Top Source Card */}
                                      <div className="w-full">
                                        <a
                                          href={categorySources[0].link}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="flex items-start p-3 bg-gray-50 rounded-lg border hover:bg-gray-100 transition w-full max-w-[85%] overflow-hidden mb-2"
                                        >
                                          <div className="text-blue-600 mr-3 mt-1 flex-shrink-0">
                                            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                              <polyline points="14,2 14,8 20,8" />
                                            </svg>
                                          </div>
                                          <div className="flex-1 min-w-0">
                                            <h3 className="text-sm font-medium text-gray-900 mb-1 truncate">{categorySources[0].name || "PDF"}</h3>
                                            <p className="text-xs text-gray-500 leading-relaxed" style={{display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden'}}>{categorySources[0].summary || "No summary available"}</p>
                                          </div>
                                        </a>
                                      </div>
                                      {/* Other Related Source Section */}
                                      {categorySources.length > 1 && (
                                        <div className="mt-4">
                                          <div className="text-xs font-semibold text-gray-600 mb-2">Other Related Source</div>
                                          <div className="space-y-2">
                                            {categorySources.slice(1).map((source, i) => (
                                              <div key={i} className="w-full">
                                                <a
                                                  href={source.link}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  className="flex items-start p-3 bg-gray-50 rounded-lg border hover:bg-gray-100 transition w-full max-w-[85%] overflow-hidden"
                                                >
                                                  <div className="text-blue-600 mr-3 mt-1 flex-shrink-0">
                                                    <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                                      <polyline points="14,2 14,8 20,8" />
                                                    </svg>
                                                  </div>
                                                  <div className="flex-1 min-w-0">
                                                    <h3 className="text-sm font-medium text-gray-900 mb-1 truncate">{source.name || "PDF"}</h3>
                                                    <p className="text-xs text-gray-500 leading-relaxed" style={{display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden'}}>{source.summary || "No summary available"}</p>
                                                  </div>
                                                </a>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            }

                            return null;
                          })()}
                          </div>


                          </div>

                          {/* Search Results Area - Only show in Answer tab */}
                          {searchSubMode === "rag" && group.responses.length > 0 && (
                            <div className="space-y-4">
                              {searchLoadingStatus && groupIdx === searchGroups.length - 1 && group.responses.length === 0 && (
                                <div className="w-full flex items-center gap-2 mb-2 px-1">
                                  <div className="flex items-center gap-1">{[0, 150, 300].map((d) => <span key={d} className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: `${d}ms` }} />)}</div>
                                  <span className="text-xs text-gray-400">{searchLoadingStatus}</span>
                                </div>
                              )}
                              {group.responses.map((msg, idx) => (
                                <div key={idx} className="w-full flex flex-col mb-4 items-start">
                                  <div className="px-5 py-4 rounded-2xl max-w-[85%] break-words border shadow-sm bg-white text-cyan-900 border-cyan-300" style={{ overflow: "visible" }}>
                                    <div dangerouslySetInnerHTML={{ __html: formatBotMessage(msg.text, msg.isSearchResponse, "search", msg.sources) }} />
                                  </div>
                                  <div className="mt-1 text-xs text-gray-400 font-medium">
                                    {msg.sender === "bot" ? `${formatTimestamp(msg.timestamp || Date.now(), true)}` : `${capitalize(msg.sender || "")} ${formatTimestamp(msg.timestamp || Date.now(), true)}`}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ));
                    })()}
                  </div>
                ) : mode === "copilot" ? (
                  /* Copilot Mode - Direct TSE Agent Communication - Chat message for First page of Copilot button hit*/
                  <div className="w-full">
                    {/* Empty State for Copilot */}
                    {copilotMessages.length === 0 && (
                      <div className="flex flex-col items-center justify-center h-96 text-gray-500">
                        <svg width="64" height="64" fill="none" stroke="currentColor" strokeWidth="1" viewBox="0 0 24 24" className="mb-4">
                          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                        </svg>
                        <p className="text-lg font-medium mb-2">TSE Copilot Mode</p>
                        <p className="text-sm text-center max-w-md">
                          Connect directly with TSE agents for advanced technical support and collaboration.
                        </p>
                      </div>
                    )}

                    {/* Copilot Messages for Chat Sender */}
                    {copilotMessages.map((msg, idx) => (
                      <div
                        key={idx}
                        className="w-full flex flex-col mb-4 items-start"
                      >
                        <div
                          className={`px-5 py-4 rounded-2xl max-w-[85%] break-words border shadow-sm ${
                            msg.sender === username
                              ? isEnterprise
                                ? "bg-white text-[#1f2937] border-[#800020]"
                                : "bg-[#06b6d4] text-white border-[#06b6d4]"
                              : msg.sender === "agent"
                                ? isEnterprise
                                  ? "bg-white text-[#1f2937] border-[#2563eb]"
                                  : "bg-purple-50 text-purple-900 border-purple-300"
                                : msg.sender === "system"
                                  ? "bg-green-50 text-green-900 border-green-300"
                                  : isEnterprise
                                    ? "bg-white text-[#1f2937] border-[#67e8f9]"
                                    : "bg-white text-cyan-900 border-cyan-300"
                          }`}
                          style={{
                            whiteSpace: "pre-line",
                            fontFamily: "'Space Grotesk', ui-sans-serif, system-ui, -apple-system, 'system-ui', 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif",
                            fontSize: "14px"
                          }}
                        >
                          {msg.sender === username ? (
                            <span className="whitespace-pre-wrap">{msg.text}</span>
                          ) : (
                            <div dangerouslySetInnerHTML={{ __html: formatBotMessage(msg.text, false, "copilot") }} />
                          )}
                        </div>
                        <div className="mt-1 text-xs text-gray-400 font-medium flex items-center gap-1.5" style={{maxWidth: '85%'}}>
                          <span>
                          {msg.sender === username
                            ? `You ${formatTimestamp(msg.timestamp || Date.now())}`
                            : msg.sender === "agent"
                              ? `${capitalize(localStorage.getItem('TseAgentName'))} ${formatTimestamp(msg.timestamp || Date.now())}`
                              : `${capitalize(msg.sender || "")} ${formatTimestamp(msg.timestamp || Date.now())}`}
                          </span>
                          {msg.cache_hit && <CacheIndicator />}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  /* Diagnostic Mode - Original Layout */
                  console.log("Message = ", message),
                  <>
                    {messages.map((msg, idx) => (
                      console.log("Msg = ", msg),
                      <div
                        key={idx}
                        className="w-full flex flex-col mb-4 items-start"
                      >
                        <div
                          data-sender={msg.sender}
                          className={
                            isEnterprise && msg.sender !== username
                              ? "max-w-full break-words text-cyan-900"
                              : `px-5 py-4 rounded-2xl max-w-[85%] break-words border shadow-sm ${
                                  msg.sender === username
                                    ? isEnterprise
                                      ? "bg-white text-[#1f2937] border-[#800020]"
                                      : "bg-[#06b6d4] text-white border-[#06b6d4]"
                                    : msg.sender === "agent"
                                      ? isEnterprise
                                        ? "bg-white text-[#1f2937] border-[#2563eb]"
                                        : "bg-purple-50 text-purple-900 border-purple-300"
                                      : (
                                          inRecommendation && msg.sender === "bot" && recommendationStartIdx !== null && idx >= recommendationStartIdx
                                            ? "bg-green-100 text-green-900 border-green-300"
                                            : isEnterprise
                                              ? "bg-white text-[#1f2937] border-[#67e8f9]"
                                              : "bg-white text-cyan-900 border-cyan-300"
                                        )
                                }`
                          }
                          style={{
                            whiteSpace: "pre-line",
                            fontFamily: "'Space Grotesk', ui-sans-serif, system-ui, -apple-system, 'system-ui', 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif",
                            fontSize: isEnterprise && msg.sender !== username ? "18px" : "14px"
                          }}
                        >
                          {msg.sender === username ? (
                            (/Router#|\n.*\n|\t|\s{2,}/.test(msg.text) ? (
                              <pre style={{ fontFamily: 'IBM Plex Mono, Menlo, monospace', fontSize: 12, margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', background: 'none', border: 'none', padding: 0 }}>{msg.text}</pre>
                            ) : (
                              <span className="whitespace-pre-wrap">{msg.text}</span>
                            ))
                          ) : (
                            <div dangerouslySetInnerHTML={{ __html: formatBotMessage(msg.text, msg.isSearchResponse, "diagnostic", msg.sources) }} />
                          )}
                        </div>
                        {!isEnterprise && (
                        <div className="mt-1 text-xs text-gray-400 font-medium flex items-center gap-1.5" style={{maxWidth: '85%'}}>
                          <span>
                          {msg.sender === username
                            ? `You ${formatTimestamp(msg.timestamp || Date.now())}`
                            : msg.sender === "bot"
                              ? `${msg.sender_name || (participants.find(p => p.role === "bot")?.name) || "AI Agent"} ${formatTimestamp(msg.timestamp || Date.now())}`
                              : msg.sender === "agent"
                                ? `${capitalize((participants.find(p => p.role === "agent")?.name)|| "Live Agent")} ${formatTimestamp(msg.timestamp || Date.now())}`
                                : `${capitalize(msg.sender || "")} ${formatTimestamp(msg.timestamp || Date.now())}`}
                          </span>
                          {msg.cache_hit && <CacheIndicator />}
                        </div>
                        )}
                      </div>
                    ))}
                    
                    {/* Typing indicator for diagnostic mode */}
                    {showTypingIndicator && !escalationActive && mode === "diagnostic" && (
                      <div className="w-full flex flex-col mb-4 items-start">
                        <div className="px-5 py-4 rounded-2xl max-w-[85%] break-words border shadow-sm bg-white text-cyan-900 border-cyan-300">
                          <div className="flex items-center space-x-2">
                            <div className="flex space-x-1">
                              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                            </div>
                            <span className="text-sm text-gray-500">{getCurrentAgentName()} is typing...</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* Enterprise view: chat input centered directly under the greeting */}
                {isEnterprise && (
                  <div className="w-full mt-6">
                    {chatInputBox}
                  </div>
                )}
              </div>
            </main>
          </div>
        </div>
        </div>
        )}

        {/* Fade gradient + pinned chat input — only in the default view.
            The enterprise view renders the input centered under the greeting
            (inside <main> above). */}
        {!isEnterprise && (
          <>
            {/* Fade gradient above chat input */}
            <div
              className="fixed pointer-events-none"
              style={{
                left: '50%',
                transform: 'translateX(-50%)',
                width: '100%',
                maxWidth: '64rem', // Match max-w-4xl
                height: '40px', // Reduced height
                bottom: '120px', // Adjusted position
                background: 'linear-gradient(to top, white 0%, white 40%, transparent 100%)',
                zIndex: 40
              }}
            />

            {/* Fixed Chat Input and Utility Bar */}
            <div className="fixed bottom-0 left-1/2 transform -translate-x-1/2 bg-white z-50 w-full max-w-4xl">
              <div className="w-full mx-auto px-6 pb-4">
                {/* Chat Input */}
                {chatInputBox}

                {/* Utility Bar with Mode Toggle */}
                {utilityBar}
              </div>
            </div>
          </>
        )}

        {/* Sources Panel - Bottom-right popup */}
        <div 
          className="fixed right-4 bg-[#FAFAF6] border border-[#EBECE6] rounded-lg shadow-lg transition-all duration-300 ease-in-out flex flex-col z-40"
          style={{ 
            width: '300px',
            maxHeight: '400px',
            bottom: '35%', // Position it 35% from the bottom
            transform: sourcesPanelOpen ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.95)',
            opacity: sourcesPanelOpen ? 1 : 0,
            visibility: sourcesPanelOpen ? 'visible' : 'hidden'
          }}
        >
          <div className="flex items-center justify-between p-3 border-b border-[#EBECE6]">
            <h2 className="text-base font-semibold text-[#253439]">
              {sidebarMode === "sources" ? "Sources" : "Search Sources"}
            </h2>
            <button
              onClick={() => setSourcesPanelOpen(false)}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#E6F0EF] transition"
              type="button"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-[#253439]"
              >
                <path d="M18 6L6 18" />
                <path d="M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Fix: wrap sidebarMode conditional in curly braces for valid JSX */}
          {sidebarMode === "search" ? (
            // Search Sources Content
            <div className="flex-1 overflow-y-auto">
              {searchSources.map((source) => (
                <div
                  key={source.id}
                  className="flex items-center justify-between px-4 py-3 hover:bg-[#E6F0EF] cursor-pointer border-b border-[#EBECE6]"
                  onClick={() => toggleSearchSource(source.id)}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-[#06b6d4]">{source.icon}</span>
                    <div>
                      <div className="text-[#253439] text-sm font-medium">{source.title}</div>
                      <div className="text-[#6B7280] text-xs">{source.description}</div>
                    </div>
                  </div>
                  <div className={`w-10 h-6 rounded-full p-1 transition-colors duration-200 ease-in-out ${source.active ? 'bg-[#06b6d4]' : 'bg-gray-300'}`}>
                    <div className={`w-4 h-4 rounded-full transition-transform duration-200 ease-in-out transform ${source.active ? 'translate-x-4 bg-white' : 'translate-x-0 bg-white'}`}></div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            // Diagnostic Sources Content
            <div className="flex-1 overflow-y-auto p-4">
              <div className="flex flex-col space-y-3">
                {diagnosticSources.map((src, idx) => (
                  <a
                    key={idx}
                    href={src.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center bg-white hover:bg-[#E6F0EF] transition rounded-lg p-3 shadow-sm border border-[#EBECE6]"
                    style={{ textDecoration: "none" }}
                  >
                    {/* Icon logic */}
                    <div className={`w-6 h-6 rounded-lg flex items-center justify-center mr-2 flex-shrink-0 ${getCategoryColor(getCategoryFromDocumentName(src.title)).bg}`}>
                      {(() => {
                        const categoryColors = getCategoryColor(getCategoryFromDocumentName(src.title));
                        return src.icon === "pdf" ? (
                          <svg
                            width="14"
                            height="14"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                            className={categoryColors.iconColor}
                          >
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <polyline points="14,2 14,8 20,8" />
                            <line x1="16" y1="13" x2="8" y2="13" />
                            <line x1="16" y1="17" x2="8" y2="17" />
                            <polyline points="10,9 9,9 8,9" />
                          </svg>
                        ) : (
                          <div className={`w-3 h-3 ${categoryColors.bg.replace('bg-', 'bg-').replace('-100', '-300')} rounded`}></div>
                        );
                      })()}
                    </div>

                      {/* Title */}
                      <span className="text-sm text-[#253439] line-clamp-2">
                        {src.title}
                      </span>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* === AVATAR COMPONENT === */}
          {avatarPanelOpen && (
            <div 
              className="fixed flex flex-col p-4 pt-12 transition-all duration-300 ease-in-out z-[140]"
              style={{ top: '8px', right: '8px', width: '320px' }}
            >
              {/* Avatar Container */}
              <div className="relative w-full rounded-lg overflow-hidden border border-[#EBECE6]">
                <InteractiveAvatar
                  ref={avatarRef}
                  onStatusChange={handleAvatarStatusChange}
                  avatarId={botAvatarId}
                />
                {/* Close button overlay */}
                <button
                  onClick={() => {
                    setAvatarPanelOpen(false);
                    console.log("🎭 Closed avatar panel");
                  }}
                  className="absolute top-2 right-2 w-6 h-6 bg-black bg-opacity-50 rounded-full flex items-center justify-center text-white hover:bg-opacity-70 transition z-10"
                  title="Close avatar panel"
                  type="button"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M18 6L6 18"/>
                    <path d="m6 6 12 12"/>
                  </svg>
                </button>
              </div>
            </div>
          )}

          {/* === VIDEO PANEL === */}
          {videoOpen && (
            <div 
              className="fixed bg-[#FAFAF6] flex flex-col p-4 pt-6 transition-all duration-300 ease-in-out z-50"
              style={{ top: '8px', right: '8px', width: '320px' }}
            >
              {/* Video Container */}
              <div className="relative w-full h-48 bg-[#FAFAF6] rounded-lg overflow-hidden border border-[#EBECE6]">
                <video
                  ref={videoRef}
                  width="100%"
                  height="100%"
                  autoPlay
                  muted
                  className="w-full h-full object-cover"
                  style={{
                    background: "#FAFAF6",
                    transform: "scaleX(-1)"
                  }}
                />
                {/* Close button overlay */}
                <button
                  onClick={handleStopVideo}
                  className="absolute top-2 right-2 w-6 h-6 bg-black bg-opacity-50 rounded-full flex items-center justify-center text-white hover:bg-opacity-70 transition"
                  title="Close video"
                  type="button"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M18 6L6 18"/>
                    <path d="m6 6 12 12"/>
                  </svg>
                </button>
              </div>
            </div>
          )}

          {agentNotification && (
            <div className="fixed top-0 left-0 right-0 z-50 bg-yellow-200 text-yellow-900 text-center py-2 shadow">
              {agentNotification}
            </div>
          )}

          {/* Image Zoom Modal */}
          {zoomImageUrl && <ImagePreviewModal imageUrl={zoomImageUrl} onClose={closeZoom} />}


        </div>
      
    </div>
  );
}

export default ChatWindow;
